import { useState, useEffect, useRef, useCallback } from 'react';
import { getOrCreateInstallationId } from '../lib/installationIdentity';
import { useAuth } from '../contexts/AuthContext';
import { useBusiness } from '../contexts/BusinessContext';
import { useCRMAuth } from '../contexts/CRMAuthContext';
import { auth, getCurrentIdToken } from '../lib/firebase';
import { createTranscriptCapture } from '../lib/pcmTranscriptCapture';
import { emitConversationActivity } from '../lib/conversationRealtime';
import {
  STAFFCALL_PROTO,
  explicitHandoff, isWinnerEventForPending, sdpTargetFor, isNoAnswer,
  callerDisplayName, canRingInvite, shouldDismissInvite, isArmedOfferForUs,
} from '../lib/staffCall';
import { shouldAnswerAsMediaOffer, shouldSuppressGenericRing } from '../lib/handoffOfferRouting';
import { acceptOperatorCallSession, mediaRoomContractFor } from '../lib/operatorCallAccept';
import { acquireCallMedia, classifyMediaError, MEDIA_ERROR } from '../lib/mediaDevices';
import { shouldSuppressStartFailure, shouldRaiseNoAnswer } from '../lib/callFailureGuard';
// D16-B — operator-side auxiliary supervision leg (canonical media module; primary Customer↔Operator PC
// is never referenced by these; live audio proof is a Milestone-6 gate).
import { buildOperatorSupervisionLeg, answerOperatorSupervisionLeg, addSupervisorIce } from '../lib/supervisionMedia';
import { tenantHeaders } from '../lib/tenantSession';

// Diagnostics are OFF in production. Enable on demand for a session with:
//   localStorage.setItem('webrtc_debug','1')  (then reload)
// This gates the verbose [WEBRTC-DIAG] console logs, the getStats poller, and the on-screen overlay.
const WEBRTC_DEBUG = (() => { try { return window.localStorage.getItem('webrtc_debug') === '1'; } catch (e) { return false; } })();
const dbg = (...a) => { if (WEBRTC_DEBUG) console.log(...a); };

// Media-resilience tuning (symmetric with Android/iOS). The caller/offerer initiates bounded ICE
// restarts; the callee holds the session and awaits the caller's restart offer (no glare). A
// transient disconnected gets a grace window to self-heal before recovery kicks in.
const RESTART_MAX_ATTEMPTS = 2;
const DISCONNECT_GRACE_MS = 4000;
const RESTART_ATTEMPT_TIMEOUT_MS = 10000;
const CALLEE_WAIT_MS = 22000;

export const useWebRTC = () => {
  const { user } = useAuth();
  const { currentBusiness } = useBusiness();
  const { makeAuthenticatedRequest, user: crmUser } = useCRMAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [currentCall, setCurrentCall] = useState(null);
  const [incomingCall, setIncomingCall] = useState(null);
  // P0-D: app-scoped incoming customer-handoff alert. Rings on EVERY authenticated Business route so a
  // logged-in operator is never unreachable off the Operator Workspace page (mirrors the Android Business
  // app-scoped HandoffSignalObserver). Populated only when the Operator Workspace's rich handler is NOT
  // mounted (mutually exclusive → no double-ring); the desktop keeps its existing queue/accept behavior.
  const [incomingHandoff, setIncomingHandoff] = useState(null);
  // App-wide operator-call (encounter / AI-Voice) ring for an operator NOT on the Operator Workspace.
  const [operatorCallRing, setOperatorCallRing] = useState(null);
  // AUDIO for an operator-call invite, independent of WHICH surface shows it.
  //
  // The ringtone effect covered incomingCall (staff) and incomingHandoff (handoff) only. An operator-call
  // invite handled by the Operator Workspace lands in that component's own `opCallInvite` state, which
  // the effect cannot see — so the card has ALWAYS appeared in silence, on every build. That is why a
  // scheduled consultation "did not ring out" even while the backend logged
  // `[OPCALL] ring … ws=1 reachable=True`. Runtime-observed 2026-09-07 on enc_appt_5d5ebbfa79cf.
  //
  // This is a RING SIGNAL, not a second ring UI: it never draws anything. The card (workspace) or the
  // global alert (any other route) still owns the visual, so there is exactly one surface either way.
  const [operatorCallRinging, setOperatorCallRinging] = useState(null);
  // DEDICATED dedup, deliberately NOT `handoffSeenRef`. Sharing that map let a handoff's seen/resolved
  // state suppress a legitimate first encounter ring — one of the two causes of the silent-ring
  // regression rolled back on 2026-09-07.
  const opCallSeenRef = useRef(new Map());
  // Sessions the canonical operator-call ring has POSITIVELY CLAIMED. The generic staff ring stands down
  // for a session only once it appears here; otherwise it remains the safety net.
  const opCallClaimedRef = useRef(new Set());
  // The customer's SDP offer routinely arrives BEFORE the operator clicks Answer (the patient joins and
  // offers within a second; the operator answers seconds later). Suppressing the generic ring for a
  // claimed session must NOT discard that offer: `joinMediaRoom` then arms the room and waits forever
  // for an offer that already came and went — "Connecting…" with a blank remote AND blank local preview.
  // Runtime-proven 2026-09-07 on enc_appt_5d5ebbfa79cf (offer 16:01:59, accept 16:02:55, no answer ever).
  // Android only escaped this because its FCM wake reconnects the socket, which makes the backend replay
  // the stored offer; a browser socket never reconnects, so there is no replay to rely on.
  const pendingOperatorOfferRef = useRef(null);
  const [localStream, setLocalStream] = useState(null);
  // M4 — the ACTIVE media room's call id, exposed so a surface can recognise a governed
  // ENCOUNTER (`enc_<appointment_id>`) without reaching into refs. Null for no call, and for a
  // staff-to-staff call it is either null or a non-encounter id — both correctly mean
  // "not an encounter", so staff calling is unaffected.
  const [activeCallId, setActiveCallId] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  // ACCUMULATED remote tracks for the CURRENT call, keyed by track id.
  //
  // `ontrack` fires once PER TRACK. Installing `event.streams[0]` on each firing is last-wins, and is
  // only safe while every remote track shares one msid. An Android Customer video consultation published
  // the mic and the camera under two DIFFERENT stream ids, so the video firing replaced the audio stream
  // and the <audio> sink was left holding a stream with no audio track - video rendered, nothing was
  // audible. Accumulating by track id makes the receiver independent of how a sender groups its msids.
  const remoteTracksRef = useRef(new Map());
  // D16-B — the operator's auxiliary supervision leg (separate PC; never the primary call). Closed on call end.
  const supervisorLegRef = useRef(null);
  // ADDITIVE (Phase C, P0-E media-truth): TRUE only while a real, live REMOTE audio track is present
  // (ontrack live audio / receiver). This — not merely "a call object exists" — is what flips the desk
  // from "Connected — no live audio" to "Human Call Active". Reset on new interaction + on call end.
  const [hasRemoteAudio, setHasRemoteAudio] = useState(false);
  const [callStatus, setCallStatus] = useState('idle'); // idle, connecting, connected, ended
  // P0 (Business Web team calls): inline call-state error (device-aware, non-blocking). Replaces the
  // blocking alert()s. { code, message } or null. Cleared on new attempt / answer / connected / remote track.
  const [callError, setCallError] = useState(null);
  const [isReconnecting, setIsReconnecting] = useState(false); // media degraded — recovering via ICE restart
  const [isOnHold, setIsOnHold] = useState(false); // LOCAL hold only (remote side is not signaled yet)
  const [mediaPhase, setMediaPhase] = useState('idle'); // ADDITIVE (Phase C): AI-Voice handoff media ladder
  // ---- TEMPORARY DIAGNOSTIC INSTRUMENTATION (remove after root cause proven) ----
  const [diag, setDiag] = useState(null);                          // live getStats snapshot for overlay
  const [iceInfo, setIceInfo] = useState({ stun: 0, turn: 0, fetched: false, source: '' });
  const [wsReconnects, setWsReconnects] = useState(0);
  const diagTimerRef = useRef(null);
  const wsReconnectRef = useRef(0);
  const [iceServers, setIceServers] = useState([{ urls: ['stun:stun.l.google.com:19302'] }]);
  // Firebase user is the RELIABLE identity (the context user objects are empty on some pages). Track
  // it so the WS connects as soon as auth is ready.
  const [fbUid, setFbUid] = useState(auth?.currentUser?.uid || null);
  useEffect(() => {
    if (!auth?.onAuthStateChanged) return;
    const unsub = auth.onAuthStateChanged((u) => setFbUid(u?.uid || null));
    return () => unsub();
  }, []);

  const ws = useRef(null);
  const peerConnection = useRef(null);
  // P0 (false-failure guard): monotonic call-generation. A failure from an older generation must never
  // fail a newer connecting/connected call. Bumped on every initiate/accept/media-join.
  const callGenRef = useRef(0);
  // Screen-share ownership (deterministic senders; restore camera on stop; no duplicate senders).
  const screenSharingRef = useRef(false);
  const screenSenderRef = useRef(null);      // the sender currently carrying the screen track (if we added one)
  const screenAddedSenderRef = useRef(false); // true when screen share ADDED a video sender (voice call)
  const currentCallIdRef = useRef(null);   // per-call id to match offer/answer/ice/call-end
  const callPeerRef = useRef(null);        // signaling target for THIS call (set synchronously before pc) — ICE/offer/answer routing must not read stale `currentCall` state
  const pendingCandidatesRef = useRef([]); // ICE candidates that arrived before pc.remoteDescription was set — buffered, then flushed
  const externalConfHandlerRef = useRef(null); // ADDITIVE: conference module subscribes to conf-* signaling (does not affect 1:1 calls)
  const externalOperatorCallHandlerRef = useRef(null); // ADDITIVE: in-app operator call ring subscribes to operator-call-* signaling (does not affect 1:1 calls)
  const externalHandoffHandlerRef = useRef(null); // ADDITIVE: AI human-handoff alert subscribes to operator-handoff-* signaling (does not affect 1:1 calls)
  // P0-D: bounded dedup for the global handoff alert — interaction_id → 'ringing' | 'resolved'. Prevents
  // a duplicate ring/modal on repeat events and stops an already-resolved replay from ringing again.
  const handoffSeenRef = useRef(new Map());
  // P0 (web accept/media): single-flight guard so a double-click / duplicate Answer sends exactly one accept.
  const acceptingHandoffRef = useRef(false);

  // ── PEER CONNECTION OWNERSHIP ──────────────────────────────────────────────────────────────────
  //
  // THE HOLE THIS CLOSES. `peerConnection.current` is written by THREE transactions -- initiateCall
  // (caller), acceptCall (answerer) and handleMediaRoomOffer (handoff/encounter answerer) -- and each
  // of them used to RE-READ that ref for addTrack / createOffer / createAnswer / setLocalDescription
  // AFTER awaiting. The widest await is getMediaStream, which can sit on a mic/camera permission
  // prompt for seconds. If another writer replaced the ref inside that window, the first transaction
  // finished its negotiation against a PeerConnection that had never consumed its offer, whose
  // signalingState was therefore `stable`, and the browser threw
  //   InvalidStateError: cannot create an answer in a state other than have-remote-offer
  // The displaced PC was also never closed, so its ICE and track handlers stayed live.
  //
  // This was never a regression: no web generation has ever had an ownership mechanism. Earlier
  // WebRTC race work hardened SIGNALLING (socket registry, offer replay, msid accumulation, staff
  // call election); `acceptingHandoffRef` hardened ONE ui entry point. None of them established who
  // owns the PeerConnection.
  //
  // THE INVARIANT. For one call_id, exactly one transaction owns exactly one RTCPeerConnection. Only
  // the owning transaction may negotiate on it. A new owner explicitly closes the one it displaces.
  // Cleanup is always conditional on generation, so an older transaction unwinding late can never
  // tear down a newer one. Operator login, role and tenant are untouched by any of this.
  const pcGenerationRef = useRef(0);
  const pcOwnerRef = useRef(null);        // { generation, callId } — who owns peerConnection.current
  const acceptInFlightRef = useRef(null); // { callId, generation } — single-flight, bound to a call

  /**
   * Take ownership of `pc` for `callId`, closing whatever it displaces. Returns this transaction's
   * generation, which the caller must hold for its whole lifetime and pass to every later check.
   */
  const claimPeerConnection = (pc, callId) => {
    const displaced = peerConnection.current;
    if (displaced && displaced !== pc) {
      // Explicit close: a silently overwritten PC keeps firing ICE and track events against state
      // that no longer owns the call.
      try { displaced.close(); } catch (e) { /* already closed */ }
    }
    const generation = (pcGenerationRef.current += 1);
    try { pc.__servisfyPcId = generation; } catch (e) { /* non-extensible in exotic hosts */ }
    pcOwnerRef.current = { generation, callId: callId ?? null };
    peerConnection.current = pc;
    return generation;
  };

  /** Does this transaction still own the connection it prepared? Checked after every await. */
  const ownsPeerConnection = (pc, generation, callId) => {
    const owner = pcOwnerRef.current;
    return !!owner
      && owner.generation === generation
      && owner.callId === (callId ?? null)
      && peerConnection.current === pc;
  };

  /** One deterministic shape for every abort, so a displaced transaction is never silent. */
  const logOwnershipAbort = (where, { pc, generation, callId }) => {
    console.warn(`[WebRTC] ${where}: aborted, PeerConnection ownership lost`, {
      callId: callId ?? null,
      expectedGeneration: generation,
      currentGeneration: pcOwnerRef.current ? pcOwnerRef.current.generation : null,
      currentOwnerCallId: pcOwnerRef.current ? pcOwnerRef.current.callId : null,
      expectedPcIdentity: generation,
      currentPcIdentity: peerConnection.current ? peerConnection.current.__servisfyPcId ?? null : null,
      signalingState: pc ? pc.signalingState : null,
    });
  };
  const pendingOfferRef = useRef(null);    // incoming offer held while ringing (until Accept)
  // socket.onmessage is assigned ONCE per connection, so every handler it reaches closes over the
  // render in which the socket was opened. Reading `incomingCall`/`currentCall` state there yields
  // the value at CONNECT time (null), which is why a loser's ring could never be dismissed by a
  // cancel. These refs always hold the live value; the socket-bound handlers read them instead.
  const incomingCallRef = useRef(null);
  const currentCallRef = useRef(null);
  const ringTimeoutRef = useRef(null);     // caller-side no-answer timeout
  // Media-resilience recovery (ICE restart). isCallerRef gates initiation to the offerer only.
  const recoveryTimerRef = useRef(null);   // per-attempt (caller) / hold deadline (callee)
  const graceTimerRef = useRef(null);      // transient-disconnect grace window
  const isCallerRef = useRef(false);       // true when THIS session placed the call (offerer)
  const recoveringRef = useRef(false);
  const restartAttemptsRef = useRef(0);
  // Phase B call-logging lifecycle (status from real signaling, never from duration):
  const callMetaRef = useRef(null);        // {callId, callerUid, calleeUid, callType}
  const callStartedAtRef = useRef(null);   // ISO
  const callAnsweredAtRef = useRef(null);  // ISO (set only when truly answered/connected)
  const callOutcomeRef = useRef(null);     // terminal non-answered outcome: missed|rejected|failed
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  // Fresh handle to the operator's local mic stream. The pc event handlers close over the render in
  // which createPeerConnection() ran, so reading the `localStream` STATE there is stale (null) — that
  // is why the operator-mic transcript capture never started (P0-G). Read the mic track from this ref.
  const localStreamRef = useRef(null);
  const ringCtxRef = useRef(null);      // lazily-created AudioContext for the incoming ringtone
  const ringTimerRef = useRef(null);    // interval that repeats the ring beep while ringing
  // ADDITIVE (Phase C): AI-Voice human-handoff media bridge (operator = answerer).
  // PHASE 1 staffcall1 — caller waits for the winning callee SESSION before generating any SDP.
  const staffRingRef = useRef(null);        // { callId, resolve } — pending winner election
  const staffInviteRef = useRef(null);      // callee: the invite we are ringing on (NO SDP)
  const staffArmedRef = useRef(null);       // callee: call_id we accepted; answer its offer in place
  const answererArmedRef = useRef(false);    // when true, an inbound offer for our call_id is answered in-place (no ring)
  const mediaRoomRef = useRef(null);         // { room_id, call_id, interactionId, businessId, operator_display_name }
  const transcriptCaptureRef = useRef(null); // operator local-mic → LINEAR16 segment capture handle
  const mediaDeadlineRef = useRef(null);     // bounded connect-deadline timer (→ media_failed)
  const mediaConnectedRef = useRef(false);   // set once real media is up (dedupe media-state report)

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://api.servisfy.com';
  const wsUrl = backendUrl.replace('http://', 'ws://').replace('https://', 'wss://');

  // Initialize WebRTC configuration
  useEffect(() => {
    fetchICEServers();
  }, []);

  // Base staff identity. AuthContext `user` can be empty on some pages, so also fall back to the
  // CRM auth user (Firebase uid is always present there for signed-in staff). Without this the WS
  // never connected (Online Now = 0 across all roles).
  const baseUid = fbUid || user?.id || user?.uid || user?.staff_id || crmUser?.uid || crmUser?.customer_id || null;
  // Unique per-session id so the SAME staff can be online from multiple browsers/devices at once
  // (each session gets its own signaling connection; backend dedups presence by base uid).
  const sessionIdRef = useRef(`${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`);
  const selfId = baseUid ? `${baseUid}__${sessionIdRef.current}` : null;
  // DR-013 reconnect resilience state.
  const pingIntervalRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const wsBackoffRef = useRef(0);
  // AUTHENTICATED_SIGNALING_V1 transport state (plane business_web). Socket-authentication transport
  // only — no media/offer/answer/ICE change. Monotonic epoch so a stale callback from a superseded
  // socket can never mutate current-socket state; the token is fetched fresh and sent as the FIRST
  // onopen frame (never in the URL); auth rejection never falls back to legacy.
  const wsAuthEpochRef = useRef(0);
  const wsAuthStateRef = useRef('none');            // none | sent | ok (telemetry/state only)
  const wsConnNonceRef = useRef(null);              // per-connection-cycle nonce (non-authoritative)
  const wsTokenRefreshedThisCycleRef = useRef(false); // one forced token refresh per cycle on expiry
  const wsNeedForcedRefreshRef = useRef(false);
  const wsAuthRejectedRef = useRef(false);          // hard rejection latch: stop reconnecting, never legacy

  // WebSocket connection management
  // Depend ONLY on the stable primitives (brandId + selfId). Previously this also depended on the
  // whole currentBusiness/user/crmUser objects, whose identity changes on every context re-render —
  // that tore down and re-opened the socket repeatedly (the code-1006 reconnect churn, and a window
  // where in-flight offer/answer/ICE could be lost). Now the socket opens once per stable identity.
  useEffect(() => {
    if (currentBusiness?.brandId && selfId) {
      // Fresh identity (login / UID / tenant change) → reset the auth cycle so a previously-rejected
      // account's latch cannot block the new account and the new socket opens with a fresh token + nonce.
      wsAuthRejectedRef.current = false;
      wsTokenRefreshedThisCycleRef.current = false;
      wsNeedForcedRefreshRef.current = false;
      wsConnNonceRef.current = null;
      wsAuthStateRef.current = 'none';
      connectWebSocket();
    }
    return () => {
      if (reconnectTimerRef.current) { clearTimeout(reconnectTimerRef.current); reconnectTimerRef.current = null; }
      if (pingIntervalRef.current) { clearInterval(pingIntervalRef.current); pingIntervalRef.current = null; }
      if (ws.current) {
        // Bump the epoch so the OLD socket's onopen/onmessage/onclose become no-ops (no-stale guard) —
        // a superseded account's callbacks must never mutate the new socket's state.
        wsAuthEpochRef.current++;
        ws.current.close();
      }
    };
  }, [currentBusiness?.brandId, selfId]);

  // DR-013: when the session-resilience layer recovers the token (laptop wake / network restore), it
  // broadcasts crm:session-recover. If our socket isn't OPEN, reconnect immediately with the fresh token
  // (reset backoff) instead of waiting out the exponential timer.
  useEffect(() => {
    const onRecover = () => {
      if (!currentBusiness?.brandId || !selfId) return;
      if (ws.current && ws.current.readyState === WebSocket.OPEN) return;
      wsBackoffRef.current = 0;
      if (reconnectTimerRef.current) { clearTimeout(reconnectTimerRef.current); reconnectTimerRef.current = null; }
      connectWebSocket();
    };
    window.addEventListener('crm:session-recover', onRecover);
    return () => window.removeEventListener('crm:session-recover', onRecover);
  }, [currentBusiness?.brandId, selfId]);

  const countIce = (servers, source) => {
    let stun = 0, turn = 0;
    (servers || []).forEach((s) => {
      [].concat(s.urls || s.url || []).forEach((u) => {
        if (/^turns?:/i.test(u)) turn++;
        else if (/^stun:/i.test(u)) stun++;
      });
    });
    setIceInfo({ stun, turn, fetched: true, source });
    dbg('[WEBRTC-DIAG] ice-servers', JSON.stringify({ stun, turn, turnPresent: turn > 0, source }));
  };

  const fetchICEServers = async () => {
    try {
      // SEC-ICE-01. This was a bare `fetch` with no credentials, under the comment
      // "/api/v1/webrtc routes are public — no auth header required". The route mints REAL Twilio TURN
      // credentials (username + credential in the body), so "public" meant anyone on the internet could
      // harvest working relay credentials — a direct cost and abuse vector, and the first thing a
      // penetration tester reaches for.
      //
      // Every other consumer of this endpoint was already authenticated: both Android planes attach the
      // bearer globally via AuthInterceptor, both iOS planes via the axios request interceptor, and
      // ManageOperators already used makeAuthenticatedRequest. This call site was the ONLY unauthenticated
      // one on the platform, which is why the server could not yet require auth.
      //
      // Uses the SAME canonical authenticated client the rest of the CRM uses — it resolves the live
      // Firebase user, attaches the bearer and carries the active-tenant header. No second token
      // mechanism, no token in the URL. The fallback below is unchanged, so a transient failure degrades
      // to public STUN exactly as it does today rather than breaking media.
      const response = await makeAuthenticatedRequest('/api/v1/webrtc/ice-servers', { method: 'GET' });
      if (response.ok) {
        const config = await response.json();
        setIceServers(config.iceServers);
        countIce(config.iceServers, 'backend');
      }
    } catch (error) {
      console.error('Failed to fetch ICE servers:', error);
      const fallback = [{ urls: ['stun:stun.l.google.com:19302'] }];
      setIceServers(fallback);
      countIce(fallback, 'fallback(fetch-failed)');
    }
  };

  const connectWebSocket = async () => {
    // Don't open a second socket if one is already live/connecting (prevents reconnect storms).
    if (ws.current && (ws.current.readyState === WebSocket.OPEN || ws.current.readyState === WebSocket.CONNECTING)) {
      return;
    }
    if (wsAuthRejectedRef.current) return; // hard auth rejection latched — do NOT reconnect-storm, never legacy
    const brandId = currentBusiness?.brandId;
    if (!brandId || !selfId) return;
    // AUTHENTICATED_SIGNALING_V1: pre-fetch a FRESH Firebase ID token BEFORE opening the socket so the
    // auth envelope can be the FIRST onopen frame. forceRefresh only after an auth-expiry (one per
    // cycle); DR-013 session-recover still force-refreshes via this path. The token is NEVER placed in
    // the URL (the legacy token query param is removed) — only the non-secret auth_protocol=v1 marker.
    const forceRefresh = wsNeedForcedRefreshRef.current;
    wsNeedForcedRefreshRef.current = false;
    let token = '';
    try {
      token = (auth?.currentUser ? await auth.currentUser.getIdToken(forceRefresh) : getCurrentIdToken()) || '';
    } catch (e) {
      token = getCurrentIdToken() || '';
    }
    // Superseded while awaiting the token (identity change / a socket became live / hard rejection)?
    if (wsAuthRejectedRef.current) return;
    if (ws.current && (ws.current.readyState === WebSocket.OPEN || ws.current.readyState === WebSocket.CONNECTING)) return;
    if (currentBusiness?.brandId !== brandId || !selfId) return;

    const epoch = ++wsAuthEpochRef.current;
    const nonce = `${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
    wsConnNonceRef.current = nonce;
    wsAuthStateRef.current = 'none';
    // PHASE 1 (staffcall1): advertise direct-call protocol capability EXPLICITLY at connect. The
    // backend treats the ABSENCE of this token as legacy — capability is never inferred from
    // platform, User-Agent, session-id shape, registration order or timing. Set on every connect,
    // so a reconnect re-advertises it (the server replaces, never unions, per conn_id).
    const wsEndpoint = `${wsUrl}/api/v1/webrtc/${brandId}/${selfId}?auth_protocol=v1&proto=${STAFFCALL_PROTO}`;
    console.debug('[WebRTC] connecting', { business: brandId, selfId });

    const socket = new WebSocket(wsEndpoint);
    ws.current = socket;

    socket.onopen = () => {
      if (epoch !== wsAuthEpochRef.current || ws.current !== socket) return; // superseded socket — ignore
      // AUTH FRAME FIRST — the literal first frame, before ping / any offer/answer/ICE. The old
      // default-off backend ignores it; the new backend authenticates before registration. We do NOT
      // wait for auth_ok (rollback-safe). Never log the envelope or token.
      if (token) {
        try {
          socket.send(JSON.stringify({
            type: 'auth',
            protocol: 'AUTHENTICATED_SIGNALING_V1',
            token,
            plane: 'business_web',
            nonce,
            // RT-3/Phase 3e: additive durable installation_id (opaque per-browser-installation; NOT the
            // per-socket nonce/session, NOT an auth credential). Preserves every existing field + ordering.
            installation_id: getOrCreateInstallationId(),
          }));
          wsAuthStateRef.current = 'sent';
        } catch (e) { /* legacy backend still functions without the auth frame */ }
      }
      console.log('WebRTC WebSocket connected');
      setIsConnected(true);
      wsBackoffRef.current = 0; // reset backoff on a healthy connection
      // Heartbeat — store in a ref and clear any prior interval so reconnects don't leak timers.
      if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = setInterval(() => {
        if (ws.current?.readyState === WebSocket.OPEN) {
          ws.current.send(JSON.stringify({ type: 'ping' }));
        } else if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }
      }, 30000);
    };

    socket.onmessage = handleWebSocketMessage;

    socket.onclose = (e) => {
      if (epoch !== wsAuthEpochRef.current || ws.current !== socket) return; // superseded socket — ignore
      wsReconnectRef.current += 1;
      setWsReconnects(wsReconnectRef.current);
      dbg('[WEBRTC-DIAG] WS close', JSON.stringify({ code: e?.code, reason: e?.reason, duringActiveCall: !!currentCallIdRef.current, totalCloses: wsReconnectRef.current }));
      setIsConnected(false);
      if (pingIntervalRef.current) { clearInterval(pingIntervalRef.current); pingIntervalRef.current = null; }
      wsAuthStateRef.current = 'none';
      // Auth rejection (server close 4401) — NEVER fall back to legacy; refresh once then stop.
      if (e?.code === 4401) { onWsAuthRejected(false); return; }
      wsTokenRefreshedThisCycleRef.current = false; // ordinary drop → fresh cycle
      // DR-013: exponential backoff + jitter + cap instead of a fixed 3s hammer — prevents a reconnect
      // storm against a downed backend while still recovering quickly after a brief flap.
      const attempt = (wsBackoffRef.current = (wsBackoffRef.current || 0) + 1);
      const delay = Math.min(30000, 1000 * Math.pow(2, Math.min(attempt, 5))) + Math.floor(Math.random() * 1000);
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = setTimeout(() => {
        if (currentBusiness?.brandId && selfId && !wsAuthRejectedRef.current) {
          connectWebSocket();
        }
      }, delay);
    };

    socket.onerror = (error) => {
      if (epoch !== wsAuthEpochRef.current || ws.current !== socket) return; // superseded socket — ignore
      console.error('WebRTC WebSocket error:', error);
    };
  };

  // Auth rejection (server close 4401 or coded auth_error/auth_rejected frame). NEVER fall back to
  // legacy. Allow exactly one forced-refresh retry per connection cycle; a second rejection latches
  // wsAuthRejectedRef and stops the reconnect loop so the existing safe session UX shows instead of
  // storming. Does not touch media / PeerConnection.
  const onWsAuthRejected = (closeFirst) => {
    if (closeFirst) {
      wsAuthEpochRef.current++;              // invalidate the rejected socket's callbacks
      try { ws.current?.close(); } catch (e) { /* ignore */ }
      setIsConnected(false);
    }
    if (!wsTokenRefreshedThisCycleRef.current) {
      wsTokenRefreshedThisCycleRef.current = true;
      wsNeedForcedRefreshRef.current = true;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = setTimeout(() => {
        if (currentBusiness?.brandId && selfId && !wsAuthRejectedRef.current) connectWebSocket();
      }, 500);
    } else {
      wsAuthRejectedRef.current = true;
    }
  };

  // P0-D: global (route-independent) handoff alert lifecycle. Dedupes by canonical interaction id,
  // rings once on 'incoming', and clears on any terminal event (accepted/cancelled/expired/resolved).
  const HANDOFF_TERMINAL = ['operator-handoff-accepted', 'operator-handoff-cancelled',
    'operator-handoff-expired', 'operator-handoff-resolved', 'operator-handoff-cancel'];
  // Global operator-call ring for an operator who is not on the Operator Workspace. It CLAIMS the
  // session, which is what allows the generic ring to stand down for it; if this never runs, the generic
  // ring still fires and the operator is never left in silence.
  const handleGlobalOperatorCall = (message) => {
    const id = message.call_session_id || message.call_id || null;
    if (!id) return;
    if (message.type === 'operator-call-invite') {
      const seen = opCallSeenRef.current.get(id);
      if (seen === 'ringing' || seen === 'resolved') return;
      opCallSeenRef.current.set(id, 'ringing');
      if (opCallSeenRef.current.size > 50) {
        const oldest = opCallSeenRef.current.keys().next().value;
        opCallSeenRef.current.delete(oldest);
      }
      opCallClaimedRef.current.add(id);          // the claim that permits generic suppression
      setOperatorCallRing({
        interaction_id: id,
        call_session_id: id,
        customer_name: message.customer_name || null,
        call_type: message.call_type === 'video' ? 'video' : 'voice',
        channel: message.call_type === 'video' ? 'video' : 'voice',
        conversation_id: message.conversation_id || null,
        priority: 'normal',
        business_id: message.business_id || null,
      });
    } else if (message.type === 'operator-call-cancel') {
      opCallSeenRef.current.set(id, 'resolved');
      opCallClaimedRef.current.delete(id);
      if (pendingOperatorOfferRef.current && pendingOperatorOfferRef.current.call_id === id) {
        pendingOperatorOfferRef.current = null;   // never carry a dead offer into the next call
      }
      setOperatorCallRing((cur) => (cur && cur.interaction_id === id ? null : cur));
    }
  };

  const handleGlobalHandoff = (message) => {
    const id = message.interaction_id || message.call_id || message.handoff_id || null;
    if (message.type === 'operator-handoff-incoming') {
      if (!id) return;
      const seen = handoffSeenRef.current.get(id);
      if (seen === 'ringing' || seen === 'resolved') return; // dedup repeat / already-resolved replay
      handoffSeenRef.current.set(id, 'ringing');
      if (handoffSeenRef.current.size > 50) {
        const oldest = handoffSeenRef.current.keys().next().value;
        handoffSeenRef.current.delete(oldest);
      }
      setIncomingHandoff({
        interaction_id: id,
        customer_name: message.customer_name || null,
        channel: message.channel || 'voice',
        priority: message.priority || 'normal',
        business_id: message.business_id || null,
      });
    } else if (HANDOFF_TERMINAL.indexOf(message.type) !== -1) {
      // DR-012: a terminal event mutates the active ring ONLY when it is correlated to the SAME
      // interaction. A null/missing interaction_id is ignored for teardown so a stray terminal cannot
      // clear whichever interaction is currently ringing (Interaction A's event can't clear B).
      if (!id) {
        console.warn('[handoff] terminal event with no interaction_id ignored for teardown', { type: message.type });
        return;
      }
      handoffSeenRef.current.set(id, 'resolved');
      setIncomingHandoff((cur) => (cur && cur.interaction_id === id ? null : cur));
    }
  };

  // D16-B — operator auxiliary supervision leg. Builds/answers a SEPARATE aux PeerConnection from an
  // authenticated supervisor-offer and adds the LISTEN FEED (customer-remote + operator-local audio) as
  // ADDITIONAL tracks. The primary Customer↔Operator PC (peerConnection.current) is never referenced here,
  // never renegotiated, and no customer is ever a supervision target. Manager whisper audio is played to
  // the OPERATOR output only. Live audio behavior is a Milestone-6 acceptance item.
  const handleSupervisorSignal = async (message) => {
    // P0-D observability (diagnostic-only): a SAFE supervisor-diag beacon over the operator's EXISTING
    // call socket. Names/counts/states only — never SDP/ICE bodies/tokens. Never alters call/supervision
    // media behavior; confined to this supervisor branch (the base Customer↔Operator path is untouched).
    const emitDiag = (event, extra, msg) => {
      try { ws.current?.send(JSON.stringify({ type: 'supervisor-diag', event, role: 'operator',
        call_id: msg && msg.call_id, supervision_session_id: msg && msg.supervision_session_id, ...(extra || {}) })); }
      catch (_) { /* diagnostics never break supervision */ }
    };
    if (message.type === 'supervisor-offer') {
      const listenTracks = [];
      const cust = remoteStream && remoteStream.getAudioTracks && remoteStream.getAudioTracks()[0];
      if (cust) listenTracks.push({ track: cust, stream: remoteStream });
      const mic = localStreamRef.current && localStreamRef.current.getAudioTracks && localStreamRef.current.getAudioTracks()[0];
      if (mic) listenTracks.push({ track: mic, stream: localStreamRef.current });
      // Observability FIRST (before any construction that could throw) so a receipt is always recorded.
      emitDiag('OPERATOR_SUPERVISOR_OFFER_RECEIVED', {
        listen_track_count: listenTracks.length,
        remote_call_track_count: cust ? 1 : 0, local_call_track_count: mic ? 1 : 0 }, message);
      // P0-D web-operator fix: mediaRoomRef.current is an OBJECT { room_id, call_id, ... } — passing it as
      // expectedCallId made the guard compare `offer.call_id (string) !== object` → ALWAYS true → threw and
      // silently dropped EVERY supervision offer during a live handoff (mediaRoomRef is set then). Compare
      // against the active call's string call_id; supervision_session_id remains the authoritative correlation.
      const leg = buildOperatorSupervisionLeg(message, {
        expectedCallId: (mediaRoomRef.current && mediaRoomRef.current.call_id) || message.call_id,
        createPeerConnection: (cfg) => new RTCPeerConnection(cfg),
        iceServers, listenTracks,
      });
      supervisorLegRef.current = leg;
      leg._opSent = 0; leg._opRecv = 0;
      emitDiag('OPERATOR_SUPERVISION_PC_CREATED', {}, message);
      // Diagnostic-only listeners (addEventListener does NOT clobber the media handlers below).
      try {
        if (leg.pc.addEventListener) {
          leg.pc.addEventListener('icecandidate', (e) => { if (e && e.candidate) leg._opSent += 1; });
          leg.pc.addEventListener('iceconnectionstatechange', () => emitDiag('OPERATOR_ICE_CONNECTION_STATE', { ice_connection_state: leg.pc.iceConnectionState, candidates_sent: leg._opSent, candidates_received: leg._opRecv }, message));
          leg.pc.addEventListener('connectionstatechange', () => emitDiag('OPERATOR_CONNECTION_STATE', { connection_state: leg.pc.connectionState }, message));
        }
      } catch (_) { /* addEventListener unsupported — diagnostics best-effort */ }
      leg.pc.ontrack = (e) => {
        // Play the manager whisper on the OPERATOR side only (a private <audio> element, not the customer).
        try { const el = new Audio(); el.autoplay = true; el.srcObject = e.streams[0]; el.play().catch(() => {}); leg._audioEl = el; } catch (_) { /* noop */ }
      };
      leg.pc.onicecandidate = (e) => {
        if (e && e.candidate) ws.current?.send(JSON.stringify({ type: 'supervisor-ice', target: message.sender,
          call_id: message.call_id, supervision_session_id: message.supervision_session_id, candidate: e.candidate }));
      };
      const answer = await answerOperatorSupervisionLeg(leg, message.offer);
      emitDiag('OPERATOR_ANSWER_CREATED', { signaling_state: leg.pc.signalingState }, message);
      ws.current?.send(JSON.stringify({ type: 'supervisor-answer', target: message.sender,
        call_id: message.call_id, supervision_session_id: message.supervision_session_id, answer }));
      emitDiag('OPERATOR_ANSWER_SENT', {}, message);
      return;
    }
    if (message.type === 'supervisor-ice' && supervisorLegRef.current) {
      supervisorLegRef.current._opRecv = (supervisorLegRef.current._opRecv || 0) + 1;
      await addSupervisorIce(supervisorLegRef.current, message.candidate);
      emitDiag('OPERATOR_ICE_CANDIDATE_RECEIVED', { candidates_received: supervisorLegRef.current._opRecv }, message);
      return;
    }
    if (message.type === 'supervisor-end' && supervisorLegRef.current) {
      try { supervisorLegRef.current.pc.close(); } catch (_) { /* noop */ }
      supervisorLegRef.current = null;
    }
  };

  const handleWebSocketMessage = async (event) => {
    const message = JSON.parse(event.data);

    // AUTHENTICATED_SIGNALING_V1 control frames (telemetry/state only — not signaling). auth_ok marks
    // the socket AUTHENTICATED_V1; a coded rejection never falls back to legacy (mirrors close 4401).
    if (message.type === 'auth_ok') {
      wsAuthStateRef.current = 'ok';
      wsTokenRefreshedThisCycleRef.current = false;
      wsAuthRejectedRef.current = false;
      return;
    }
    if (message.type === 'auth_error' || message.type === 'auth_rejected') {
      onWsAuthRejected(true);
      return;
    }

    // ADDITIVE: hand conference signaling to the conference module. 1:1 call handling below is unchanged.
    if (typeof message.type === 'string' && message.type.indexOf('conf-') === 0) {
      try { externalConfHandlerRef.current && externalConfHandlerRef.current(message); } catch (e) { /* never breaks 1:1 */ }
      return;
    }

    // ADDITIVE: hand in-app operator-call ring/cancel signaling to the operator desktop. Does not
    // affect staff↔staff 1:1 calls (distinct message types). Older clients without a handler ignore it.
    if (typeof message.type === 'string' && message.type.indexOf('operator-call-') === 0) {
      if (message.business_id && currentBusiness?.brandId && message.business_id !== currentBusiness.brandId) return;
      const _ocId = message.call_session_id || message.call_id || null;
      // Ring FIRST, regardless of which surface will show it.
      if (_ocId && message.type === 'operator-call-invite') setOperatorCallRinging(_ocId);
      if (_ocId && message.type === 'operator-call-cancel') {
        setOperatorCallRinging((cur) => (cur === _ocId ? null : cur));
      }
      if (externalOperatorCallHandlerRef.current) {
        // Operator Workspace is mounted -> it owns the card/accept/media (unchanged behaviour). It is
        // also a positive claim, so the generic ring may stand down for this session.
        if (_ocId && message.type === 'operator-call-invite') opCallClaimedRef.current.add(_ocId);
        if (_ocId && message.type === 'operator-call-cancel') opCallClaimedRef.current.delete(_ocId);
        try { externalOperatorCallHandlerRef.current(message); } catch (e) { /* never breaks 1:1 */ }
      } else {
        // Operator is on another Business route: ring globally and accept in place. Same shape as the
        // P0-D handoff fallback, so an encounter is never silently dropped off the workspace.
        try { handleGlobalOperatorCall(message); } catch (e) { /* never breaks 1:1 */ }
      }
      return;
    }

    // ADDITIVE: real-time AI human-handoff alert (operator-handoff-incoming) → operator desktop
    // refreshes its queue + alerts immediately instead of waiting for the periodic poll. Distinct
    // message type; not a media ring. Older clients without a handler ignore it.
    if (typeof message.type === 'string' && message.type.indexOf('operator-handoff-') === 0) {
      // Tenant isolation: never ring for another business's handoff.
      if (message.business_id && currentBusiness?.brandId && message.business_id !== currentBusiness.brandId) return;
      if (externalHandoffHandlerRef.current) {
        // Operator Workspace is mounted → it owns the rich queue/ring/accept (unchanged behavior).
        try { externalHandoffHandlerRef.current(message); } catch (e) { /* never breaks 1:1 */ }
      } else {
        // P0-D: operator is on another Business route → global fallback ring + alert so the handoff
        // is never silently dropped (was the no-ring defect). Mutually exclusive with the desktop path.
        try { handleGlobalHandoff(message); } catch (e) { /* never breaks 1:1 */ }
      }
      return;
    }

    // D16-B — operator-side auxiliary supervision leg (separate PC; never touches the primary call).
    if (typeof message.type === 'string' && message.type.indexOf('supervisor-') === 0) {
      try { await handleSupervisorSignal(message); } catch (e) { /* never breaks the primary call */ }
      return;
    }

    switch (message.type) {
      case 'offer':
        // ADDITIVE (Phase C): when armed for an AI-Voice handoff media bridge, an inbound offer whose
        // call_id matches our media room is answered in-place (operator = answerer) — NOT rung as a 1:1
        // call. Any other offer falls through to the normal ring path (unchanged live behavior).
        if (shouldAnswerAsMediaOffer(message, { answererArmed: answererArmedRef.current, mediaRoom: mediaRoomRef.current })) {
          await handleMediaRoomOffer(message);
          break;
        }
        // The generic ring stands down ONLY for a session the canonical operator-call ring has already
        // claimed. A classified offer with no claim still rings here: removing the fallback before the
        // replacement had claimed the session is exactly what produced a SILENT ring on 2026-09-07.
        if (shouldSuppressGenericRing(message, opCallClaimedRef.current)) {
          // Hold it for the accept. Buffering is NOT answering: no media, no SDP, no camera — the
          // operator still has to click Answer, and `acceptOperatorCallRing` consumes this exactly once
          // after the server says won.
          pendingOperatorOfferRef.current = message;
          break;
        }
        handleIncomingOffer(message);   // RING — do not auto-answer
        break;
      case 'answer':
        await handleAnswer(message);
        break;
      case 'ice-candidate':
        await handleICECandidate(message);
        break;
      case 'call-end':       // sent by endCall()/rejectCall()
      case 'call-ended':
        handleCallEnded(message);
        break;
      case 'call-cancel':    // multi-device: this call was answered/handled on another of MY sessions
        handleCallCancel(message);
        break;
      // ── PHASE 1 staffcall1 ──────────────────────────────────────────────────────────────
      case 'staff-call-ringing':
        // Fan-out confirmed; keep waiting for a human to accept. Not a protocol decision.
        setCallStatus('ringing');
        break;
      case 'staff-call-winner': {
        // Election result for OUR outgoing call. Ignore anything for a different/stale call.
        const r = staffRingRef.current;
        if (isWinnerEventForPending(r, message)) {
          staffRingRef.current = null;
          r.resolve({ mode: 'winner', winner: message.winner });
        }
        break;
      }
      case 'staff-call-unsupported': {
        // A legacy sibling session exists for the callee -> whole call uses the legacy path.
        const r2 = staffRingRef.current;
        if (isWinnerEventForPending(r2, message)) {
          staffRingRef.current = null;
          r2.resolve({ mode: 'legacy' });
        }
        break;
      }
      case 'staff-call-invite':
        handleStaffCallInvite(message);   // RING — carries NO SDP
        break;
      case 'staff-call-lost':
        // Another of MY sessions won this call; clear our pending invite state.
        if (shouldDismissInvite(staffInviteRef.current, message)) {
          staffInviteRef.current = null;
          staffArmedRef.current = null;
          setIncomingCall(null);
          setCallStatus('idle');
        }
        break;
      case 'call-failed':
        handleCallFailed(message);
        break;
      case 'pong':
        // Connection alive confirmation
        break;
      case 'new_message': {
        // The backend broadcasts conversation activity on this same business socket. This hook is
        // the only thing holding that socket, so it must hand the event on — but it does NOT become
        // a messaging authority: it forwards a SIGNAL and nothing else. Subscribers refetch the
        // thread from the API, so db.messages stays the single durable truth and a duplicated or
        // dropped frame costs at most one refetch.
        emitConversationActivity({
          conversationId: message.conversation_id,
          messageId: message.message_id,
          senderType: message.sender_type,
        });
        break;
      }
      default:
        console.log('Unknown WebRTC message type:', message.type);
    }
  };

  // ---- DIAGNOSTIC stats poller (temporary) ----
  const collectStats = async () => {
    const pc = peerConnection.current;
    if (!pc) return;
    const out = {
      ts: Date.now(),
      connectionState: pc.connectionState,
      iceConnectionState: pc.iceConnectionState,
      iceGatheringState: pc.iceGatheringState,
      signalingState: pc.signalingState,
      pair: null,
      audioIn: {}, audioOut: {}, videoIn: {}, videoOut: {},
      localTracks: [], remoteTracks: [],
    };
    try {
      const stats = await pc.getStats();
      const byId = {};
      stats.forEach((r) => { byId[r.id] = r; });
      let sel = null;
      stats.forEach((r) => {
        if (r.type === 'transport' && r.selectedCandidatePairId && byId[r.selectedCandidatePairId]) sel = byId[r.selectedCandidatePairId];
        if (!sel && r.type === 'candidate-pair' && (r.selected || (r.nominated && r.state === 'succeeded'))) sel = r;
        if (r.type === 'inbound-rtp' && r.kind === 'audio') out.audioIn = { packets: r.packetsReceived || 0, bytes: r.bytesReceived || 0 };
        if (r.type === 'outbound-rtp' && r.kind === 'audio') out.audioOut = { packets: r.packetsSent || 0, bytes: r.bytesSent || 0 };
        if (r.type === 'inbound-rtp' && r.kind === 'video') out.videoIn = { packets: r.packetsReceived || 0, bytes: r.bytesReceived || 0, framesDecoded: r.framesDecoded || 0 };
        if (r.type === 'outbound-rtp' && r.kind === 'video') out.videoOut = { packets: r.packetsSent || 0, bytes: r.bytesSent || 0 };
      });
      if (sel) {
        const lc = byId[sel.localCandidateId], rc = byId[sel.remoteCandidateId];
        out.pair = {
          local: lc?.candidateType, remote: rc?.candidateType,
          rtt: sel.currentRoundTripTime, outBitrate: sel.availableOutgoingBitrate,
          bytesSent: sel.bytesSent, bytesReceived: sel.bytesReceived,
        };
      }
    } catch (e) { out.statsError = e?.message; }
    out.localTracks = pc.getSenders().map((s) => s.track).filter(Boolean).map((t) => ({ kind: t.kind, enabled: t.enabled, muted: t.muted, rs: t.readyState }));
    out.remoteTracks = pc.getReceivers().map((r) => r.track).filter(Boolean).map((t) => ({ kind: t.kind, enabled: t.enabled, muted: t.muted, rs: t.readyState }));
    dbg('[WEBRTC-DIAG]', JSON.stringify(out));
    setDiag(out);
  };
  const startDiag = () => {
    if (!WEBRTC_DEBUG) return;   // no getStats poller for normal users
    if (diagTimerRef.current) clearInterval(diagTimerRef.current);
    collectStats();
    diagTimerRef.current = setInterval(collectStats, 1500);
  };
  const stopDiag = () => {
    if (diagTimerRef.current) { clearInterval(diagTimerRef.current); diagTimerRef.current = null; }
  };

  // Incoming-call ringtone (WebAudio). Honest best-effort: browsers may keep the AudioContext
  // suspended until a user gesture — the staff has usually been interacting with the app, so it
  // resumes; if it stays blocked there is no sound (the visual IncomingCallModal still shows).
  const startRingtone = () => {
    try {
      if (ringTimerRef.current) return;                         // already ringing
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      if (!ringCtxRef.current) ringCtxRef.current = new AC();
      const ctx = ringCtxRef.current;
      if (ctx.state === 'suspended') ctx.resume().catch(() => {});
      const beep = () => {
        try {
          // Two-tone ring (double beep) once per interval.
          [0, 0.28].forEach((offset, i) => {
            const o = ctx.createOscillator();
            const g = ctx.createGain();
            o.type = 'sine';
            o.frequency.value = i === 0 ? 480 : 440;
            const t0 = ctx.currentTime + offset;
            g.gain.setValueAtTime(0.0001, t0);
            g.gain.exponentialRampToValueAtTime(0.18, t0 + 0.04);
            g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.22);
            o.connect(g); g.connect(ctx.destination);
            o.start(t0); o.stop(t0 + 0.24);
          });
        } catch (e) { /* ignore per-beep failures */ }
      };
      beep();
      ringTimerRef.current = setInterval(beep, 2200);
    } catch (e) { /* ringtone is best-effort */ }
  };

  const stopRingtone = () => {
    if (ringTimerRef.current) { clearInterval(ringTimerRef.current); ringTimerRef.current = null; }
  };

  const createPeerConnection = () => {
    if (!iceServers.length) return null;

    const pc = new RTCPeerConnection({ iceServers });

    pc.onicecandidate = (event) => {
      if (event.candidate && ws.current?.readyState === WebSocket.OPEN) {
        // Route to the per-call target REF (not `currentCall` state, which is stale/null in this
        // closure at gathering time). Sending with an undefined target dropped every candidate.
        const target = callPeerRef.current;
        dbg('[WEBRTC-DIAG] ice-candidate out', JSON.stringify({ target, hasTarget: !!target, call_id: currentCallIdRef.current }));
        ws.current.send(JSON.stringify({
          type: 'ice-candidate',
          candidate: event.candidate,
          target,
          call_id: currentCallIdRef.current
        }));
      }
    };

    pc.ontrack = (event) => {
      const t = event.track;
      dbg('[WEBRTC-DIAG] ontrack', JSON.stringify({ kind: t?.kind, enabled: t?.enabled, muted: t?.muted, readyState: t?.readyState, streams: event.streams?.length }));
      // The media-bridge offerer may attach the track WITHOUT an associated stream (event.streams empty),
      // which made setRemoteStream(undefined) drop real audio and left the desk "no live audio" despite
      // audio flowing. Fall back to a MediaStream built from the track so the <audio> sink always plays.
      // Record the track under its own id (a Map, so a repeated ontrack for the same track cannot
      // duplicate it), then publish EVERY track accumulated so far for this call. Adding audio never
      // drops video and adding video never drops audio, whatever msid the sender used.
      if (t && t.id) remoteTracksRef.current.set(t.id, t);
      let stream = null;
      try {
        const _tracks = Array.from(remoteTracksRef.current.values());
        // A NEW MediaStream each time: React compares by reference, so mutating one in place would not
        // re-run the sink effect and the newly-added track would never be bound to the <audio> element.
        if (_tracks.length) stream = new MediaStream(_tracks);
      } catch (_) { stream = null; }
      // Last-resort fallback only — never the primary path.
      if (!stream) stream = (event.streams && event.streams[0]) || null;
      if (stream) {
        setRemoteStream(stream);
        setCallError(null);   // remote media arrived → cancel any stale start-failure message
        if (remoteVideoRef.current) remoteVideoRef.current.srcObject = stream;
      }
      // P0-E media-truth: a LIVE remote audio track means real audio is being received → clears the amber
      // "Connected — no live audio" and (with ice/connection connected) shows "Human Call Active".
      if (t && t.kind === 'audio') {
        const markLive = () => { if (t.readyState === 'live') setHasRemoteAudio(true); };
        markLive();
        t.onunmute = markLive;
        t.onended = () => setHasRemoteAudio(false);
      }
    };

    pc.oniceconnectionstatechange = () => {
      dbg('[WEBRTC-DIAG] iceConnectionState=', pc.iceConnectionState);
      // Some browsers report media reachability via iceConnectionState ('connected'/'completed') while
      // connectionState lags in 'connecting' — that left the AI-Voice handoff stuck amber despite live
      // audio. Promote on either signal (idempotent via mediaConnectedRef).
      if (pc.iceConnectionState === 'connected' || pc.iceConnectionState === 'completed') {
        promoteMediaConnected();
      }
    };
    pc.onicegatheringstatechange = () => {
      dbg('[WEBRTC-DIAG] iceGatheringState=', pc.iceGatheringState);
    };

    pc.onconnectionstatechange = () => {
      dbg('[WEBRTC-DIAG] connectionState=', pc.connectionState);
      if (pc.connectionState === 'connected') {
        if (ringTimeoutRef.current) { clearTimeout(ringTimeoutRef.current); ringTimeoutRef.current = null; }
        if (recoveringRef.current) console.debug('[WebRTC] media recovery: reconnected');
        clearRecovery();
        setCallStatus('connected');
        setCallError(null);   // connected → cancel any stale start-failure message (false-failure guard)
        startDiag();
        // ADDITIVE (Phase C): AI-Voice media bridge — real media is up NOW → human_call_active
        // (never on accept). Also reachable via iceConnectionState. Guarded so 1:1 calls are unaffected.
        promoteMediaConnected();
      } else if (pc.connectionState === 'disconnected') {
        // Transient — do NOT tear down. Grace window to self-heal; then escalate to recovery.
        if (callAnsweredAtRef.current) scheduleRecovery('disconnected');
        else endCall();
      } else if (pc.connectionState === 'failed') {
        // Media path gave up. Live call → recover (caller restarts / callee holds); else honest end.
        if (callAnsweredAtRef.current) beginRecovery('failed');
        else endCall();
      }
    };

    return pc;
  };

  // Device-aware local media acquisition. Returns the stream (unchanged signature for existing callers)
  // and records, on refs, whether video actually succeeded and any degrade reason — so initiateCall can
  // proceed as an audio call (with an inline notice) instead of hard-failing when there is no camera.
  // Throws a CLASSIFIED media error (err.code / err.userMessage) — never the old generic string.
  const lastMediaVideoRef = useRef(false);
  const lastMediaDegradedRef = useRef(null);
  const getMediaStream = async (video = false) => {
    try {
      const { stream, video: gotVideo, degraded } = await acquireCallMedia({ wantVideo: video });
      lastMediaVideoRef.current = gotVideo;
      lastMediaDegradedRef.current = degraded;
      setLocalStream(stream);
      localStreamRef.current = stream;   // fresh handle for pc-handler closures (transcript capture)
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
      return stream;
    } catch (error) {
      // error is already classified (code + userMessage) by acquireCallMedia; keep a safe console line.
      console.error('Failed to get media stream:', error?.code || error?.name || 'unknown');
      throw (error && error.code) ? error : classifyMediaError(error, { wantVideo: video });
    }
  };

  // Post one staff call-log lifecycle event. Backend upserts by (business_id, call_id) and never
  // downgrades a terminal status, so caller AND callee can both post safely (no double-count).
  // keepalive:true lets the terminal event survive a hangup/page-close.
  const postCallLog = async (status) => {
    try {
      const meta = callMetaRef.current;
      if (!meta || !meta.callId || !currentBusiness?.brandId) return;
      // STAFF CALLS ONLY. /webrtc/call-logs/{biz}/events is the staff-to-staff log and correctly refuses a
      // non-staff participant ("Both participants must be tenant staff", 403). An operator call has a
      // CUSTOMER on the other end, so posting it there was always a guaranteed 403 - runtime-observed on a
      // live Clinic consultation. Its lifecycle is already recorded by the operator-call authority.
      // mediaRoomRef is set only by joinMediaRoom, i.e. exactly for operator/handoff rooms.
      if (mediaRoomRef.current) return;
      const terminal = ['completed', 'ended', 'missed', 'rejected', 'failed'].includes(status);
      // Call-log audit is authenticated (backend require_id_token). Use a FRESH Firebase ID token
      // (getIdToken auto-refreshes near expiry) instead of a stale localStorage token — that stale token
      // was the cause of the /call-logs/.../events 401s. Best-effort: if no valid token, skip silently
      // (never send a guaranteed-401); NEVER trigger auth recovery/logout and NEVER block the call/media.
      let token = '';
      try {
        token = (auth?.currentUser ? await auth.currentUser.getIdToken() : getCurrentIdToken()) || '';
      } catch (e) {
        token = getCurrentIdToken() || '';
      }
      if (!token) return; // no valid token → skip telemetry rather than emit a 401
      fetch(`${backendUrl}/api/v1/webrtc/call-logs/${currentBusiness.brandId}/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...tenantHeaders() },
        keepalive: true,
        body: JSON.stringify({
          call_id: meta.callId,
          caller_uid: meta.callerUid,
          callee_uid: meta.calleeUid,
          call_type: meta.callType || 'voice',
          status,
          started_at: callStartedAtRef.current || null,
          answered_at: callAnsweredAtRef.current || null,
          ended_at: terminal ? new Date().toISOString() : null,
        }),
      }).catch(() => {});
    } catch (e) { /* call-logging is best-effort, never blocks the call */ }
  };

  const initiateCall = async (targetUserId, callType = 'voice') => {
    // One click → one call generation. A failure from a prior generation cannot fail this attempt.
    const myGen = (callGenRef.current = callGenRef.current + 1);
    setCallError(null);   // clear any inline error from a previous attempt
    try {
      // Check if WebSocket is connected
      if (!ws.current || ws.current.readyState !== WebSocket.OPEN) {
        throw new Error('WebSocket not connected. Please ensure you are logged in with a valid business profile.');
      }

      const callId = `call_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
      currentCallIdRef.current = callId;
      callPeerRef.current = targetUserId;   // ICE candidates route here (same target as the offer)
      isCallerRef.current = true;           // THIS session is the offerer → it initiates ICE restarts
      setCallStatus('connecting');

      // Get media stream FIRST so a device problem is surfaced BEFORE we advertise a live call — and so a
      // video call with no camera degrades to audio (never the old generic failure).
      const stream = await getMediaStream(callType === 'video');
      // If a video call had no camera, proceed as an audio call and show an inline, non-blocking notice.
      const effectiveType = (callType === 'video' && lastMediaDegradedRef.current === 'no_camera') ? 'voice' : callType;
      if (callType === 'video' && effectiveType === 'voice') {
        setCallError({ code: MEDIA_ERROR.NO_DEVICE, message: 'No camera was found — starting an audio-only call.' });
      }

      setCurrentCall({
        targetUser: targetUserId,
        type: effectiveType,
        isInitiator: true,
        callId
      });
      // Call-log lifecycle (caller): ringing.
      callMetaRef.current = { callId, callerUid: baseUid, calleeUid: targetUserId, callType: effectiveType };
      callStartedAtRef.current = new Date().toISOString();
      callAnsweredAtRef.current = null;
      callOutcomeRef.current = null;
      postCallLog('ringing');

      // Create peer connection. Owned locally for this transaction's lifetime: the ring election
      // below is awaited, so re-reading the ref afterwards could offer on another writer's PC.
      const pc = createPeerConnection();
      if (!pc) {
        throw new Error('Failed to create peer connection');
      }
      const generation = claimPeerConnection(pc, callId);

      // Add local stream to peer connection
      stream.getTracks().forEach(track => {
        pc.addTrack(track, stream);
      });

      // PHASE 1 (staffcall1): ring EVERY session of the callee first, then send SDP to the ONE that
      // accepts. Generating the offer up-front is exactly what made the SDP the ring and limited it
      // to a single session. The backend replies `staff-call-winner` (elected atomically) or
      // `staff-call-unsupported` (a legacy sibling session is present -> use the legacy path).
      let sdpTarget = targetUserId;   // resolved via sdpTargetFor() once the election lands
      const ringOutcome = await new Promise((resolve) => {
        let done = false;
        const finish = (v) => { if (!done) { done = true; staffRingRef.current = null; resolve(v); } };
        staffRingRef.current = { callId, resolve: finish };
        // NO-ANSWER timeout (same 30s as the legacy ring). This is a human-answer deadline, NOT a
        // protocol-selection timer — protocol is decided synchronously by the capability gate.
        setTimeout(() => finish({ mode: 'noanswer' }), 30000);
        try {
          ws.current.send(JSON.stringify({
            type: 'staff-call-ring',
            target: targetUserId,
            call_type: effectiveType,
            call_id: callId,
          }));
        } catch (e) { finish({ mode: 'legacy' }); }
      });
      if (myGen !== callGenRef.current) return;   // superseded by a newer call attempt
      if (isNoAnswer(ringOutcome)) {
        // Nobody accepted within the ring window. Same terminal treatment as the legacy no-answer
        // path: mark missed, tear the attempt down through endCall(), surface the inline notice.
        console.debug('[WebRTC] staffcall1: no answer — ending ringing call.');
        callOutcomeRef.current = 'missed';
        endCall();
        setCallError({ code: 'NO_ANSWER', message: 'No answer.' });
        return;
      }
      sdpTarget = sdpTargetFor(ringOutcome, targetUserId);   // EXACT winner conn, never the base uid
      callPeerRef.current = sdpTarget;                        // ICE follows the same exact session

      // Create and send offer — only now, and only to the winner (or to the legacy target).
      // The ring election above was awaited; confirm this transaction still owns its connection
      // before generating SDP on it.
      if (!ownsPeerConnection(pc, generation, callId)) {
        logOwnershipAbort('initiateCall', { pc, generation, callId });
        return;
      }
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      ws.current.send(JSON.stringify({
        type: 'offer',
        offer: offer,
        target: sdpTarget,
        call_type: effectiveType,
        call_id: callId
      }));

      // No-answer timeout: fire ONLY if this is still the active generation, the call was never answered,
      // and signaling never reached 'stable'. "Connecting slowly" is not "failed" — a slow-but-successful
      // answer/ICE/remote-track cancels this via the guard below.
      if (ringTimeoutRef.current) clearTimeout(ringTimeoutRef.current);
      ringTimeoutRef.current = setTimeout(() => {
        const pc = peerConnection.current;
        if (shouldRaiseNoAnswer({
          attemptGen: myGen,
          currentGen: callGenRef.current,
          answered: !!callAnsweredAtRef.current,
          signalingState: pc ? pc.signalingState : null,
        })) {
          console.debug('[WebRTC] no answer — ending ringing call.');
          callOutcomeRef.current = 'missed';
          endCall();
          setCallError({ code: 'NO_ANSWER', message: 'No answer.' });
        }
      }, 30000);

    } catch (error) {
      // Only fail THIS attempt if it is still the active generation and the call has not progressed. A
      // stale/superseded attempt (or one that actually connected) must never tear down or alert.
      const suppress = shouldSuppressStartFailure({
        attemptGen: myGen,
        currentGen: callGenRef.current,
        answered: !!callAnsweredAtRef.current,
        connected: callStatus === 'connected',
        remoteAudio: hasRemoteAudio,
        iceConnected: peerConnection.current
          ? (peerConnection.current.iceConnectionState === 'connected' || peerConnection.current.iceConnectionState === 'completed')
          : false,
      });
      if (suppress) {
        console.debug('[WebRTC] initiate error suppressed (stale generation or call already progressing).');
        return { ok: false, suppressed: true };
      }
      console.error('Failed to initiate call:', error?.code || error?.name || 'unknown');
      setCallStatus('idle');
      setCurrentCall(null);
      setCallError({ code: error?.code || 'CALL_START_FAILED', message: error?.userMessage || error?.message || 'Could not start the call.' });
      return { ok: false, code: error?.code || 'CALL_START_FAILED', message: error?.userMessage || error?.message };
    }
    return { ok: true };
  };

  // Renegotiation: an ICE-restart re-offer for the call we are ALREADY in (same call_id, already
  // answered). Answer it in place on the EXISTING peer connection — no incoming modal/ring, no
  // call-end, no duplicate log, no new session.
  const handleRenegotiationOffer = async (message) => {
    const pc = peerConnection.current;
    if (!pc) return;
    try {
      if (message.sender) callPeerRef.current = message.sender;   // pin ICE to the caller's session
      console.debug('[WebRTC] same-call restart offer received');
      await pc.setRemoteDescription(message.offer);
      await flushCandidates();
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      ws.current?.send(JSON.stringify({
        type: 'answer',
        answer,
        target: message.sender,
        call_id: message.call_id,
      }));
      console.debug('[WebRTC] restart answer sent');
    } catch (error) {
      console.warn('[WebRTC] renegotiation answer failed:', error);
    }
  };

  // RING: an offer arrived. Hold it and show the IncomingCallModal — DO NOT auto-answer.
  // PHASE 1 staffcall1 — CALLEE ring. Carries NO SDP, so no peer connection is created here: a
  // losing session must never build a PeerConnection or send an answer. The caller display name is
  // SERVER-resolved and authoritative, replacing the fragile online-roster lookup that rendered the
  // literal "Staff member" whenever the caller was missing from a stale roster snapshot.
  const handleStaffCallInvite = (message) => {
    if (!canRingInvite({ currentCall: currentCallRef.current, hasPeerConnection: !!peerConnection.current })) return;
    staffInviteRef.current = message;
    currentCallIdRef.current = message.call_id || currentCallIdRef.current;
    const serverName = callerDisplayName(message, '');
    // Set the mirror SYNCHRONOUSLY as well: the sync effect only runs after commit, and a cancel
    // that arrives in the same tick must still find the invite it has to dismiss.
    const _inv = {
      caller: message.caller_uid || (message.caller || '').split('__')[0],
      callerName: serverName,
      // The ringing UI resolves its label through resolveIncomingCallerName(), which reads
      // callerInfo.name — NOT callerName. Setting only callerName silently dropped the
      // server-resolved name and fell back to the stale online-roster lookup (and then to a
      // "User <id>" placeholder), which is the caller-name defect this invite was meant to fix.
      callerInfo: serverName ? { name: serverName } : undefined,
      callType: message.call_type || 'voice',
      callId: message.call_id,
      staffCall: true,                          // marks the new lifecycle for acceptCall()
    };
    incomingCallRef.current = _inv;
    setIncomingCall(_inv);
  };

  const handleIncomingOffer = (message) => {
    // Renegotiation (ICE restart) for the call we're already in → answer in place, do not ring/reject.
    if (message.call_id && currentCallIdRef.current && message.call_id === currentCallIdRef.current &&
        peerConnection.current && callAnsweredAtRef.current) {
      handleRenegotiationOffer(message);
      return;
    }
    // If already on a call, auto-reject the new offer (busy) — clean, no auto-answer.
    if (currentCall || peerConnection.current) {
      ws.current?.send(JSON.stringify({ type: 'call-end', target: message.sender, call_id: message.call_id }));
      return;
    }
    // staffcall1: we already accepted this call's invite and WON — answer in place, never re-ring.
    if (isArmedOfferForUs(staffArmedRef.current, message)) {
      staffArmedRef.current = null;
      const inv = staffInviteRef.current;
      staffInviteRef.current = null;
      setIncomingCall(null);
      pendingOfferRef.current = message;
      acceptCall({
        message,
        incoming: {
          caller: (message.sender || '').split('__')[0],
          callType: (inv && inv.call_type) || message.call_type || 'voice',
          callId: message.call_id,
        },
      });
      return;
    }
    pendingOfferRef.current = message;
    currentCallIdRef.current = message.call_id || currentCallIdRef.current;
    setIncomingCall({
      caller: (message.sender || '').split('__')[0],   // base uid → UI resolves real name/role
      callType: message.call_type || 'voice',
      callId: message.call_id,
    });
  };

  const handleAnswer = async (message) => {
    const pc = peerConnection.current;
    // Ignore answers for a different call (multi-session rings all sessions → duplicate answers).
    if (message.call_id && currentCallIdRef.current && message.call_id !== currentCallIdRef.current) {
      console.debug('[WebRTC] answer ignored — call_id mismatch.');
      return;
    }
    // Normal duplicate/late answer or stale pc — handle quietly (not an error).
    if (!pc) {
      console.debug('[WebRTC] answer ignored — no active peer connection (stale).');
      return;
    }
    if (pc.signalingState !== 'have-local-offer') {
      console.debug('[WebRTC] answer ignored — signalingState =', pc.signalingState, '(duplicate/late).');
      return;
    }
    try {
      // Multi-device: pin this call to the EXACT session that answered, so our ICE (and any later
      // signaling) targets only that device instead of fanning out to all the callee's sessions.
      if (message.sender) callPeerRef.current = message.sender;
      await pc.setRemoteDescription(message.answer);
      await flushCandidates();   // apply any ICE candidates that arrived before the answer
      if (ringTimeoutRef.current) { clearTimeout(ringTimeoutRef.current); ringTimeoutRef.current = null; }
      // First-answer guard: stamp answered / log once and flip to 'connecting' only on the FIRST
      // answer. An ICE-restart re-answer must NOT re-log or downgrade an already-connected call.
      if (!callAnsweredAtRef.current) {
        callAnsweredAtRef.current = new Date().toISOString();
        postCallLog('answered');
        setCallError(null);   // answered → a slow-but-successful answer cancels any pending failure
        // Do NOT mark 'connected' on answer receipt — only real pc.connectionState==='connected'
        // (onconnectionstatechange) flips to connected. Until then we are media-connecting.
        setCallStatus('connecting');
      } else {
        console.debug('[WebRTC] restart answer applied (renegotiation) — not re-logging');
      }
    } catch (error) {
      console.error('Failed to handle answer:', error);
      endCall();
    }
  };

  const handleICECandidate = async (message) => {
    if (message.call_id && currentCallIdRef.current && message.call_id !== currentCallIdRef.current) return;
    if (!message.candidate) return;
    const pc = peerConnection.current;
    // Only add once the pc exists AND has a remote description; otherwise buffer (addIceCandidate
    // before setRemoteDescription throws and the candidate is lost — the real cause of ICE failing).
    if (pc && pc.remoteDescription && pc.remoteDescription.type) {
      try {
        await pc.addIceCandidate(message.candidate);
        dbg('[WEBRTC-DIAG] ice-candidate in: addIceCandidate OK');
      } catch (error) {
        dbg('[WEBRTC-DIAG] ice-candidate in: addIceCandidate FAIL', JSON.stringify({ name: error?.name, message: error?.message }));
      }
    } else {
      pendingCandidatesRef.current.push(message.candidate);
      dbg('[WEBRTC-DIAG] ice-candidate in: QUEUED (no remoteDescription yet)', JSON.stringify({ queued: pendingCandidatesRef.current.length, hasPc: !!pc }));
    }
  };

  // Flush buffered ICE candidates once the remote description is set (call on both caller & callee).
  const flushCandidates = async () => {
    const pc = peerConnection.current;
    if (!pc || !pc.remoteDescription) return;
    const queued = pendingCandidatesRef.current;
    pendingCandidatesRef.current = [];
    if (queued.length) dbg('[WEBRTC-DIAG] flushing queued ICE candidates', JSON.stringify({ count: queued.length }));
    for (const c of queued) {
      try {
        await pc.addIceCandidate(c);
        dbg('[WEBRTC-DIAG] ice-candidate flush: addIceCandidate OK');
      } catch (error) {
        dbg('[WEBRTC-DIAG] ice-candidate flush: addIceCandidate FAIL', JSON.stringify({ name: error?.name, message: error?.message }));
      }
    }
  };

  // ACCEPT: now (and only now) process the held offer → media + answer.
  // `explicit` lets the staffcall1 winner path answer WITHOUT depending on React state that was
  // just set in the same tick (setIncomingCall is async — reading it here would be a race).
  const acceptCall = async (arg) => {
    // The ringing UI binds onClick={onAccept}, so `arg` is often a click event, NOT a hand-off.
    // Discriminate by shape — treating any truthy arg as explicit skipped the invite branch below
    // and silently returned, making the call unanswerable on every session.
    const explicit = explicitHandoff(arg);
    // PHASE 1 staffcall1: an invite has NO SDP yet. Claim the call first — the backend elects the
    // winner atomically — then answer the offer it sends to this exact session. A losing session
    // never reaches the SDP path at all.
    if (!explicit && incomingCall && incomingCall.staffCall && staffInviteRef.current) {
      const inv = staffInviteRef.current;
      staffArmedRef.current = inv.call_id;      // arm: answer this call's offer in place, no ring
      // EXPOSE the call id the presentation layer selects its mode from. Purely additive: nothing
      // below reads it, and it changes no accept, signaling, offer/answer or session behaviour.
      //
      // It was set only in joinMediaRoom — the JOIN path — so a call that arrived and was ANSWERED
      // left activeCallId null. The encounter discriminator reads that value, so an answered Clinic
      // consultation could never resolve to Encounter mode and the in-call recording control never
      // mounted. A populated id does NOT imply an encounter: the selector is
      // encounterAppointmentId(), which returns null for any id without the canonical `enc_` prefix,
      // so a staff call stays in Staff mode exactly as before.
      setActiveCallId(inv.call_id || null);
      callPeerRef.current = inv.caller;         // ICE routes to the caller's exact session
      isCallerRef.current = false;
      setCallStatus('connecting');
      try {
        ws.current?.send(JSON.stringify({ type: 'staff-call-accept', call_id: inv.call_id }));
      } catch (e) { /* surfaced by the no-answer path */ }
      return;
    }
    const message = (explicit && explicit.message) || pendingOfferRef.current;
    const ic = (explicit && explicit.incoming) || incomingCall;
    if (!ic || !message) return;
    // SINGLE-FLIGHT, BOUND TO THE CALL. Three components bind the ring to acceptCall
    // (WebRTCProvider, UnifiedAgentDesktop, VoiceCalls) and an armed staffcall1 offer calls it
    // directly, so two accepts for one ring are reachable. Keyed by callId rather than a global
    // boolean so a wedged attempt can never make a LATER, different call unanswerable.
    const acceptCallId = ic.callId || currentCallIdRef.current || null;
    const inFlight = acceptInFlightRef.current;
    if (inFlight && inFlight.callId === acceptCallId) {
      console.warn('[WebRTC] accept: duplicate ignored, already in flight', {
        callId: acceptCallId, generation: inFlight.generation,
      });
      return;
    }
    acceptInFlightRef.current = { callId: acceptCallId, generation: pcGenerationRef.current + 1 };
    const myAcceptTicket = acceptInFlightRef.current;
    try {
      const callType = ic.callType || 'voice';
      const callId = ic.callId || currentCallIdRef.current;
      currentCallIdRef.current = callId;
      callPeerRef.current = message.sender;   // ICE candidates route to the caller's exact session (same target as the answer)
      isCallerRef.current = false;            // callee: holds for the caller's restart, never initiates
      setCurrentCall({
        targetUser: (message.sender || '').split('__')[0],
        type: callType,
        isInitiator: false,
        callId
      });
      setCallStatus('connecting');
      setIncomingCall(null);

      // Call-log lifecycle (callee): answered. caller = the offer sender's base uid; callee = self.
      callMetaRef.current = { callId, callerUid: (message.sender || '').split('__')[0], calleeUid: baseUid, callType };
      callAnsweredAtRef.current = new Date().toISOString();
      callOutcomeRef.current = null;
      postCallLog('answered');

      // This transaction owns `pc` for its whole lifetime. Never re-read peerConnection.current as
      // "my" connection below — that is precisely how the answer ended up on someone else's PC.
      const pc = createPeerConnection();
      if (!pc) return;
      const generation = claimPeerConnection(pc, callId);

      await pc.setRemoteDescription(message.offer);
      await flushCandidates();   // apply caller's ICE candidates that arrived while ringing (before pc existed)
      const stream = await getMediaStream(callType === 'video');
      // The wide await has passed. If another writer took ownership meanwhile, stop: it is
      // mid-negotiation on its own offer and a second answer would corrupt or throw on it.
      if (!ownsPeerConnection(pc, generation, callId)) {
        logOwnershipAbort('accept', { pc, generation, callId });
        stream.getTracks().forEach((t) => { try { t.stop(); } catch (e) { /* already ended */ } });
        return;
      }
      stream.getTracks().forEach(track => pc.addTrack(track, stream));

      // The condition the browser would otherwise surface as an opaque InvalidStateError. Assert it
      // so any future regression names itself instead of presenting as a failed call.
      if (pc.signalingState !== 'have-remote-offer' && pc.signalingState !== 'have-local-pranswer') {
        console.error('[WebRTC] accept: aborted, wrong signalingState for createAnswer', {
          callId, generation, signalingState: pc.signalingState,
          localDescription: pc.localDescription?.type ?? null,
          remoteDescription: pc.remoteDescription?.type ?? null,
        });
        return;
      }

      const answer = await pc.createAnswer();
      if (!ownsPeerConnection(pc, generation, callId)) {
        logOwnershipAbort('accept/after-createAnswer', { pc, generation, callId });
        return;
      }
      await pc.setLocalDescription(answer);
      ws.current.send(JSON.stringify({
        type: 'answer',
        answer: answer,
        target: message.sender,   // exact caller session
        call_id: callId
      }));

      pendingOfferRef.current = null;
      // Honest state: not 'connected' yet — wait for real pc.connectionState (onconnectionstatechange).
      setCallStatus('connecting');
    } catch (error) {
      console.error('Failed to accept call:', error);
      rejectCall();
    } finally {
      // CONDITIONAL. Only clear the ticket this invocation created. An older accept unwinding late
      // must never release a guard a newer transaction is relying on.
      if (acceptInFlightRef.current === myAcceptTicket) acceptInFlightRef.current = null;
    }
  };

  const reportMediaState = async (partyState) => {
    const m = mediaRoomRef.current;
    if (!m?.interactionId || !m?.businessId) return;
    try {
      await makeAuthenticatedRequest(
        `/api/v1/operator-workspace/${m.businessId}/interactions/${m.interactionId}/media-state`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ party_state: partyState }) });
    } catch (e) { /* never blocks media */ }
  };

  // Idempotent promotion to HUMAN_CALL_ACTIVE for the AI-Voice handoff media bridge. Fired from either
  // connectionState==='connected' OR iceConnectionState in {'connected','completed'} (some browsers only
  // surface one). Guarded by mediaRoomRef (no effect on 1:1 staff calls) and mediaConnectedRef (once).
  const promoteMediaConnected = () => {
    if (!mediaRoomRef.current || mediaConnectedRef.current) return;
    mediaConnectedRef.current = true;
    if (mediaDeadlineRef.current) { clearTimeout(mediaDeadlineRef.current); mediaDeadlineRef.current = null; }
    setMediaPhase('human_call_active');
    reportMediaState('media');
    startTranscriptCapture();   // operator local mic only
  };

  // Start capturing ONLY the operator's local mic → LINEAR16 segments → transcript upload. Called
  // once real media is up. The customer remote track is NEVER captured.
  const startTranscriptCapture = () => {
    const m = mediaRoomRef.current;
    if (transcriptCaptureRef.current || !m?.interactionId) return;
    // Read the mic from the REF (fresh), not the stale `localStream` state captured in the pc-handler
    // closure — that stale null is why the operator's mic segments were never captured/uploaded (P0-G).
    const ls = localStreamRef.current || localStream;
    const micTrack = ls && ls.getAudioTracks && ls.getAudioTracks()[0];
    if (!micTrack) return;
    try {
      transcriptCaptureRef.current = createTranscriptCapture({
        micTrack,
        segmentPrefix: `${m.room_id}-op`,
        upload: async (seg) => {
          await makeAuthenticatedRequest(
            `/api/v1/operator-workspace/${m.businessId}/interactions/${m.interactionId}/transcript`,
            { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(seg) });
        },
      });
      transcriptCaptureRef.current.start();
    } catch (e) { transcriptCaptureRef.current = null; }
  };

  const stopTranscriptCapture = async () => {
    const cap = transcriptCaptureRef.current;
    transcriptCaptureRef.current = null;
    if (cap) { try { await cap.stop(); } catch (e) {} }
  };

  // Join the shared WebRTC media room as ANSWERER. The customer is the offerer: we arm the offer
  // router and WAIT for the customer's SDP offer (do NOT create an offer). `contract` = media_join.
  const joinMediaRoom = (contract, meta = {}) => {
    if (!contract || !contract.call_id) {
      // Honest: accepted but no usable media contract → surface, never fake a live call.
      setMediaPhase('media_failed');
      return;
    }
    // Idempotent: already joined this room (re-accept) → do not create a second peer/room.
    if (mediaRoomRef.current && mediaRoomRef.current.call_id === contract.call_id && peerConnection.current) {
      return;
    }
    // What KIND of call this room is, is the SERVER's statement, not the caller's request.
    // The contract carries call_type; a client that could ask for video would be able to open a
    // camera on an audio-only consultation. The AI-Voice handoff contract carries no call_type, so
    // it evaluates false here and its behaviour is bit-for-bit what it was.
    const wantVideo = contract.call_type === 'video';
    mediaRoomRef.current = { ...contract, wantVideo, interactionId: meta.interactionId, businessId: meta.businessId };
    setActiveCallId((contract && contract.call_id) || null);
    currentCallIdRef.current = contract.call_id;
    isCallerRef.current = false;             // answerer: never initiates the offer
    answererArmedRef.current = true;
    mediaConnectedRef.current = false;
    callAnsweredAtRef.current = null;
    // Reset media-truth for the NEW interaction so a prior call's audio state can't carry over (P0-E).
    setHasRemoteAudio(false);
    remoteTracksRef.current.clear();   // no track may survive into the NEXT call
    setRemoteStream(null);
    // The active-call UI displays the CUSTOMER from the accepted interaction (customer_display_name),
    // NOT the raw media_join model (targetUser is null for a handoff). Thread the resolved customer
    // name onto currentCall so the connected UI never renders "User null"/a raw id.
    setCurrentCall({ targetUser: null, type: wantVideo ? 'video' : 'voice', isInitiator: false, callId: contract.call_id,
                     role: 'answerer', interactionId: meta.interactionId,
                     customerName: meta.customerName || null });
    setMediaPhase('media_joining');
    setCallStatus('connecting');
    reportMediaState('joining');
    setMediaPhase('waiting_for_customer_offer');
    // Bounded connect deadline — honest media_failed instead of a forever spinner.
    const timeoutMs = (contract.connect_timeout_seconds || 40) * 1000;
    if (mediaDeadlineRef.current) clearTimeout(mediaDeadlineRef.current);
    mediaDeadlineRef.current = setTimeout(() => {
      if (!mediaConnectedRef.current) setMediaPhase('media_failed');
    }, timeoutMs);

    // CONSUME AN OFFER THAT ARRIVED BEFORE THE ANSWER — EXACTLY ONCE.
    //
    // The patient offers within a second of joining; the operator answers seconds later. That offer was
    // held by the signalling branch (buffered, never answered — no media before accept). Now that this
    // room is armed, answer it in place. Without this the room waits for an offer that already arrived
    // and the call sits on "Connecting…" with a blank remote and a blank local preview.
    //
    // Placed HERE, not in one accept path, because both the Operator Workspace card and the app-wide
    // alert arm media through this same function. Cleared before use, and only for THIS room, so it can
    // neither answer twice nor apply a stale offer to the wrong call.
    setOperatorCallRinging(null);   // answered — stop ringing on whichever surface rang
    const _buffered = pendingOperatorOfferRef.current;
    if (_buffered && (_buffered.call_id === contract.call_id || _buffered.call_id === contract.room_id)) {
      pendingOperatorOfferRef.current = null;
      void handleMediaRoomOffer(_buffered);
    }
  };

  // Answer the customer's inbound offer in-place (modeled on the renegotiation path — no ring, no
  // incoming modal, no new session). Fires only while armed and for our call_id.
  const handleMediaRoomOffer = async (message) => {
    try {
      setMediaPhase('media_connecting');
      if (message.sender) callPeerRef.current = message.sender;   // pin ICE/answer to the customer session
      // Same ownership rule as acceptCall, for the same reason: this function is the OTHER answerer
      // writer, so it is both a cause of the displacement race and a victim of it.
      const mediaCallId = message.call_id || currentCallIdRef.current || null;
      const pc = createPeerConnection();
      if (!pc) { setMediaPhase('media_failed'); return; }
      const generation = claimPeerConnection(pc, mediaCallId);
      await pc.setRemoteDescription(message.offer);
      await flushCandidates();
      // Audio for a voice room, audio+camera for a video one. Read from the room the server
      // authorised -- never from local UI state, which is why an AI-Voice handoff still gets mic only.
      const stream = await getMediaStream(mediaRoomRef.current?.wantVideo === true);
      if (!ownsPeerConnection(pc, generation, mediaCallId)) {
        logOwnershipAbort('media-room answer', { pc, generation, callId: mediaCallId });
        stream.getTracks().forEach((t) => { try { t.stop(); } catch (e) { /* already ended */ } });
        return;   // deliberately NOT setMediaPhase: a newer owner now drives the phase
      }
      stream.getTracks().forEach(track => pc.addTrack(track, stream));
      if (pc.signalingState !== 'have-remote-offer' && pc.signalingState !== 'have-local-pranswer') {
        console.error('[WebRTC] media-room answer: aborted, wrong signalingState for createAnswer', {
          callId: mediaCallId, generation, signalingState: pc.signalingState,
          localDescription: pc.localDescription?.type ?? null,
          remoteDescription: pc.remoteDescription?.type ?? null,
        });
        setMediaPhase('media_failed');
        return;
      }
      const answer = await pc.createAnswer();
      if (!ownsPeerConnection(pc, generation, mediaCallId)) {
        logOwnershipAbort('media-room answer/after-createAnswer', { pc, generation, callId: mediaCallId });
        return;
      }
      await pc.setLocalDescription(answer);
      ws.current?.send(JSON.stringify({ type: 'answer', answer, target: message.sender,
                                        call_id: currentCallIdRef.current }));
      callAnsweredAtRef.current = new Date().toISOString();
      answererArmedRef.current = false;
      // NOT connected yet — real media is confirmed by onconnectionstatechange('connected').
    } catch (error) {
      console.warn('[WebRTC] media-room answer failed:', error);
      setMediaPhase('media_failed');
    }
  };

  const rejectCall = () => {
    const message = pendingOfferRef.current;
    if (incomingCall) {
      // Notify the caller's exact session that the call was rejected.
      ws.current?.send(JSON.stringify({
        type: 'call-end',
        target: message?.sender || incomingCall.caller,
        call_id: incomingCall.callId || currentCallIdRef.current
      }));
      setIncomingCall(null);
    }
    pendingOfferRef.current = null;
    currentCallIdRef.current = null;
    callPeerRef.current = null;
    pendingCandidatesRef.current = [];
  };

  // ---- Media recovery (ICE restart) ----
  // Cancel recovery timers + clear degraded state (on reconnect or teardown).
  const clearRecovery = () => {
    if (graceTimerRef.current) { clearTimeout(graceTimerRef.current); graceTimerRef.current = null; }
    if (recoveryTimerRef.current) { clearTimeout(recoveryTimerRef.current); recoveryTimerRef.current = null; }
    recoveringRef.current = false;
    restartAttemptsRef.current = 0;
    setIsReconnecting(false);
  };

  // Caller-only: send a bounded ICE-restart offer on the EXISTING peer connection, same call_id/peer.
  const sendRestartOffer = async () => {
    const pc = peerConnection.current;
    if (!pc || !recoveringRef.current) return;
    try {
      restartAttemptsRef.current += 1;
      console.debug(`[WebRTC] ICE restart offer sent (attempt ${restartAttemptsRef.current}/${RESTART_MAX_ATTEMPTS})`);
      const offer = await pc.createOffer({ iceRestart: true });
      await pc.setLocalDescription(offer);
      ws.current?.send(JSON.stringify({
        type: 'offer', offer, target: callPeerRef.current, call_id: currentCallIdRef.current,
      }));
    } catch (error) {
      console.warn('[WebRTC] restart offer failed:', error);
      callOutcomeRef.current = callOutcomeRef.current || 'failed';
      clearRecovery();
      endCall();
      return;
    }
    if (recoveryTimerRef.current) clearTimeout(recoveryTimerRef.current);
    recoveryTimerRef.current = setTimeout(() => {
      recoveryTimerRef.current = null;
      if (!recoveringRef.current) return;                              // onconnectionstatechange cleared it → recovered
      if (restartAttemptsRef.current < RESTART_MAX_ATTEMPTS) sendRestartOffer();
      else { callOutcomeRef.current = callOutcomeRef.current || 'failed'; clearRecovery(); endCall(); }
    }, RESTART_ATTEMPT_TIMEOUT_MS);
  };

  // Recover a live call whose media path degraded. Caller: bounded ICE restarts. Callee: hold and
  // await the caller's restart offer (no glare). Neither reconnects in budget → honest teardown.
  const beginRecovery = (reason) => {
    if (recoveringRef.current) return;
    if (!callAnsweredAtRef.current || !peerConnection.current) return;
    recoveringRef.current = true;
    if (graceTimerRef.current) { clearTimeout(graceTimerRef.current); graceTimerRef.current = null; }
    setIsReconnecting(true);
    if (isCallerRef.current) {
      console.debug(`[WebRTC] media recovery (${reason}): caller initiating ICE restart`);
      sendRestartOffer();
    } else {
      console.debug(`[WebRTC] media recovery (${reason}): callee awaiting restart offer`);
      if (recoveryTimerRef.current) clearTimeout(recoveryTimerRef.current);
      recoveryTimerRef.current = setTimeout(() => {
        recoveryTimerRef.current = null;
        if (!recoveringRef.current) return;
        if (!peerConnection.current || peerConnection.current.connectionState !== 'connected') {
          callOutcomeRef.current = callOutcomeRef.current || 'failed';
          clearRecovery();
          endCall();
        }
      }, CALLEE_WAIT_MS);
    }
  };

  // Transient disconnected: grace window to self-heal before escalating to recovery.
  const scheduleRecovery = (reason) => {
    if (recoveringRef.current || graceTimerRef.current) return;
    if (!callAnsweredAtRef.current) return;
    setIsReconnecting(true);
    graceTimerRef.current = setTimeout(() => {
      graceTimerRef.current = null;
      const pc = peerConnection.current;
      if (pc && pc.connectionState !== 'connected') beginRecovery(reason);
      else setIsReconnecting(false);
    }, DISCONNECT_GRACE_MS);
  };

  const endCall = () => {
    if (ringTimeoutRef.current) { clearTimeout(ringTimeoutRef.current); ringTimeoutRef.current = null; }
    if (recoveryTimerRef.current) { clearTimeout(recoveryTimerRef.current); recoveryTimerRef.current = null; }
    if (graceTimerRef.current) { clearTimeout(graceTimerRef.current); graceTimerRef.current = null; }
    recoveringRef.current = false;
    restartAttemptsRef.current = 0;
    isCallerRef.current = false;
    setIsReconnecting(false);
    // Call-log terminal event: completed iff truly answered; else the real outcome (missed/rejected/failed).
    if (callMetaRef.current) {
      const terminal = callAnsweredAtRef.current ? 'completed' : (callOutcomeRef.current || 'missed');
      postCallLog(terminal);
    }
    // Send call end message
    if (currentCall && ws.current?.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify({
        type: 'call-end',
        target: currentCall.targetUser,
        call_id: currentCallIdRef.current
      }));
    }
    currentCallIdRef.current = null;
    callPeerRef.current = null;
    pendingCandidatesRef.current = [];
    pendingOfferRef.current = null;

    // D16-B — a supervised call ending closes ONLY the auxiliary supervision leg (never the reverse).
    if (supervisorLegRef.current) {
      try { supervisorLegRef.current.pc.close(); } catch (_) { /* noop */ }
      supervisorLegRef.current = null;
    }

    // Clean up streams — RELEASE THE LIVE HANDLE, NOT JUST THE REACT STATE.
    //
    // `localStreamRef` exists precisely because the state is stale inside closures (see where it is set:
    // "fresh handle for pc-handler closures"), and endCall IS such a closure. Releasing only `localStream`
    // and then nulling the ref could leave the MICROPHONE OPEN with no handle left to stop it: the track
    // keeps running, the next call captures a contended device, and the operator is inaudible to the
    // customer while everything else — signalling, accept, video — looks correct. Stop whichever handle
    // is real, then drop both.
    const _live = localStreamRef.current || localStream;
    if (_live && typeof _live.getTracks === 'function') {
      _live.getTracks().forEach((track) => { try { track.stop(); } catch (e) { /* already ended */ } });
    }
    if (localStream) setLocalStream(null);
    localStreamRef.current = null;
    // Stop every accumulated remote track, not just those in the last published stream.
    remoteTracksRef.current.forEach((track) => { try { track.stop(); } catch (e) { /* already ended */ } });
    remoteTracksRef.current.clear();
    if (remoteStream) {
      remoteStream.getTracks().forEach(track => { try { track.stop(); } catch (e) { /* already ended */ } });
      setRemoteStream(null);
    }
    setHasRemoteAudio(false);   // P0-E: drop media-truth on call end (badge → "Call Ended", not amber)

    // Clean up peer connection AND its ownership record together, so the next transaction starts
    // from a clean slate rather than inheriting a dangling owner.
    if (peerConnection.current) {
      peerConnection.current.close();
      peerConnection.current = null;
    }
    pcOwnerRef.current = null;
    // The accept guard is released only if it belongs to the call being ended. A stale endCall (an
    // old recovery timer, a late call-end for a previous call) must not free a guard a newer accept
    // is relying on -- the rule is: never clean up on behalf of a newer generation.
    if (acceptInFlightRef.current) {
      const endingCallId = currentCallIdRef.current ?? null;
      if (acceptInFlightRef.current.callId === endingCallId) acceptInFlightRef.current = null;
    }

    // ADDITIVE (Phase C): AI-Voice media bridge cleanup — flush+stop the local-mic transcript capture,
    // drop room refs. No-op for ordinary 1:1 staff calls (mediaRoomRef stays null).
    if (mediaDeadlineRef.current) { clearTimeout(mediaDeadlineRef.current); mediaDeadlineRef.current = null; }
    answererArmedRef.current = false;
    mediaConnectedRef.current = false;
    if (transcriptCaptureRef.current) { stopTranscriptCapture(); }
    if (mediaRoomRef.current) { setMediaPhase('ended'); mediaRoomRef.current = null; }
    else { setMediaPhase('idle'); }
    // M4 — no call, no encounter, no capture control. Placed AFTER the if/else so the existing
    // media-phase branch is untouched.
    setActiveCallId(null);

    // Reset call state
    setCurrentCall(null);
    setCallStatus('idle');
    setIsOnHold(false);
    // Screen-share ownership reset so the next call starts clean (no leftover sender/sharing state).
    screenSharingRef.current = false;
    screenSenderRef.current = null;
    screenAddedSenderRef.current = false;
    stopDiag();
    // Reset call-log lifecycle refs
    callMetaRef.current = null;
    callStartedAtRef.current = null;
    callAnsweredAtRef.current = null;
    callOutcomeRef.current = null;
  };

  const handleCallEnded = (message) => {
    const cid = message?.call_id;
    // Ignore a call-end for a different call.
    if (cid && currentCallIdRef.current && cid !== currentCallIdRef.current) return;
    // Multi-device guard: once we are CONNECTED to a specific session (callPeerRef is the exact
    // conn we answered/were answered by), ignore a stray call-end from a DIFFERENT session — e.g.
    // a sibling device of the callee declining after another of their devices already answered.
    // Without this, that stray call-end tore down the live call ("couldn't hear until I signed out
    // the other device").
    if (callAnsweredAtRef.current && message?.sender && callPeerRef.current &&
        message.sender !== callPeerRef.current) {
      console.debug('[WebRTC] ignoring stray call-end from non-connected session:', message.sender);
      return;
    }
    // Remote ended the call. If we never answered, the remote side rejected/canceled.
    if (callMetaRef.current && !callAnsweredAtRef.current && !callOutcomeRef.current) {
      callOutcomeRef.current = 'rejected';
    }
    endCall();
  };

  // Multi-device: another of MY OWN sessions answered/handled this call (backend "answered
  // elsewhere"). Silently dismiss the incoming ring here — do NOT send call-end to the caller,
  // which would drop the call that the other device just answered.
  const handleCallCancel = (message) => {
    const cid = message?.call_id;
    const ic = incomingCallRef.current;   // NOT the state: this handler is bound once to the socket
    if (ic && (!cid || ic.callId === cid)) {
      stopRingtone();
      setIncomingCall(null);
      incomingCallRef.current = null;
      staffInviteRef.current = null;      // a dismissed staffcall1 invite must not stay claimable
      staffArmedRef.current = null;
      pendingOfferRef.current = null;
      // Clear per-call refs only if we haven't begun our own answer for this call.
      if (!currentCallRef.current) {
        currentCallIdRef.current = null;
        callPeerRef.current = null;
        pendingCandidatesRef.current = [];
      }
    }
  };

  const handleCallFailed = (message) => {
    console.log('Call failed:', message.reason);
    if (callMetaRef.current) {
      callOutcomeRef.current = 'failed';
      postCallLog('failed');
    }
    setCallStatus('idle');
    setCurrentCall(null);
    callMetaRef.current = null;
    callStartedAtRef.current = null;
    callAnsweredAtRef.current = null;
    callOutcomeRef.current = null;
  };

  const toggleMute = () => {
    if (localStream) {
      const audioTrack = localStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        return !audioTrack.enabled;
      }
    }
    return false;
  };

  const toggleVideo = () => {
    if (localStream) {
      const videoTrack = localStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        return !videoTrack.enabled;
      }
    }
    return false;
  };

  // LOCAL hold only: we stop sending our own media and mute remote playback on OUR side.
  // The remote peer is NOT signaled (no hold message exists yet), so this is honestly a local hold —
  // the other party simply hears/sees silence. Returns the new hold state.
  const toggleHold = () => {
    const next = !isOnHold;
    if (localStream) {
      // Stop transmitting our mic/cam while held; restore when resumed.
      localStream.getTracks().forEach((t) => { t.enabled = !next; });
    }
    if (remoteVideoRef.current) {
      // Mute remote playback locally while held (operator doesn't hear the other party).
      remoteVideoRef.current.muted = next;
    }
    setIsOnHold(next);
    return next;
  };

  const outboundVideoBytes = async () => {
    try {
      const pc = peerConnection.current; if (!pc) return null;
      const stats = await pc.getStats(); let b = null;
      stats.forEach((r) => { if (r.type === 'outbound-rtp' && r.kind === 'video') b = r.bytesSent || 0; });
      return b;
    } catch { return null; }
  };

  // Renegotiate the EXISTING call after the outgoing track set changed (screen add/remove during a voice
  // call). Caller-side re-offer for the same call_id/peer; the remote answers in place (renegotiation).
  const renegotiateOffer = async () => {
    const pc = peerConnection.current;
    if (!pc || ws.current?.readyState !== WebSocket.OPEN) return;
    try {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      ws.current.send(JSON.stringify({
        type: 'offer', offer, target: callPeerRef.current, call_id: currentCallIdRef.current,
      }));
      dbg('[WEBRTC-DIAG] screenshare renegotiation offer sent');
    } catch (e) { dbg('[WEBRTC-DIAG] screenshare renegotiation failed', JSON.stringify({ name: e?.name })); }
  };

  // Screen share is CAMERA-INDEPENDENT: it uses getDisplayMedia (never getUserMedia for a camera), so it
  // works on a workstation with no camera. During a call it replaces the outgoing video track when one
  // exists (video call), or ADDS one video sender and renegotiates when none exists (voice call). Stopping
  // restores the prior camera track if any, else drops to no-video — never ending the audio call. Cancelling
  // the browser picker is benign (no crash, no camera error). A second share reuses the same sender.
  const startScreenShare = async () => {
    if (!peerConnection.current) {
      const e = new Error('Start a call first, then share your screen.');
      e.code = 'NO_ACTIVE_CALL';
      throw e;
    }
    if (screenSharingRef.current) {
      // Already sharing — do not create a duplicate sender; return the current screen track.
      return screenSenderRef.current?.track || null;
    }
    let screenStream;
    try {
      screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: { displaySurface: 'monitor' },
        audio: false,
      });
    } catch (err) {
      dbg('[WEBRTC-DIAG] screenshare picker ERROR', JSON.stringify({ name: err?.name }));
      // Cancelling the picker (NotAllowedError/AbortError) is NOT a failure and NOT a camera error.
      if (err?.name === 'NotAllowedError' || err?.name === 'AbortError') {
        const e = new Error('Screen sharing was cancelled.');
        e.code = 'SCREEN_SHARE_CANCELLED';
        throw e;
      }
      const c = classifyMediaError(err, { wantVideo: true });
      c.userMessage = 'Could not start screen sharing.';
      c.message = c.userMessage;
      throw c;
    }
    try {
      const screenTrack = screenStream.getVideoTracks()[0];
      const pc = peerConnection.current;
      const existingVideoSender = pc.getSenders().find(s => s.track?.kind === 'video');
      // Remember the camera track (if a video call) so we can restore it when sharing stops.
      const priorCamTrack = existingVideoSender?.track || null;

      if (existingVideoSender) {
        await existingVideoSender.replaceTrack(screenTrack);   // video call → swap camera→screen (no reneg)
        screenSenderRef.current = existingVideoSender;
        screenAddedSenderRef.current = false;
        dbg('[WEBRTC-DIAG] screenshare replaceTrack OK');
      } else {
        // Voice call → no video transceiver yet: add one and renegotiate so the screen is actually sent.
        const sender = pc.addTrack(screenTrack, screenStream);
        screenSenderRef.current = sender;
        screenAddedSenderRef.current = true;
        await renegotiateOffer();
        dbg('[WEBRTC-DIAG] screenshare addTrack + renegotiate');
      }
      screenSharingRef.current = true;

      const restore = async () => {
        if (!screenSharingRef.current) return;
        screenSharingRef.current = false;
        const sender = screenSenderRef.current;
        screenSenderRef.current = null;
        try {
          if (sender && priorCamTrack) {
            await sender.replaceTrack(priorCamTrack);        // restore camera (video call)
          } else if (sender && screenAddedSenderRef.current) {
            try { pc.removeTrack(sender); } catch (_) { await sender.replaceTrack(null).catch(() => {}); }
            await renegotiateOffer();                        // voice call → back to no-video
          }
        } catch (e) { dbg('[WEBRTC-DIAG] screenshare restore failed', JSON.stringify({ name: e?.name })); }
        screenAddedSenderRef.current = false;
        try { screenTrack.stop(); } catch (_) {}
        // NOTE: the audio call is never touched here — only the video sender is restored/removed.
      };
      screenTrack.onended = restore;   // browser "Stop sharing"
      return screenTrack;
    } catch (err) {
      // Attaching failed — release the display capture so we don't leak it.
      try { screenStream.getTracks().forEach(t => t.stop()); } catch (_) {}
      screenSharingRef.current = false;
      dbg('[WEBRTC-DIAG] screenshare attach ERROR', JSON.stringify({ name: err?.name }));
      throw err;
    }
  };

  const fetchOnlineUsers = useCallback(async () => {
    if (!currentBusiness?.brandId || !makeAuthenticatedRequest) return;

    try {
      const response = await makeAuthenticatedRequest(`/api/v1/webrtc/online-users/${currentBusiness.brandId}`);
      if (response.ok) {
        const data = await response.json();
        setOnlineUsers(data.users || []);
      }
    } catch (error) {
      console.error('Failed to fetch online users:', error);
    }
  }, [currentBusiness, makeAuthenticatedRequest]);

  // Fetch online users periodically
  useEffect(() => {
    if (isConnected) {
      fetchOnlineUsers();
      const interval = setInterval(fetchOnlineUsers, 10000); // Every 10 seconds
      return () => clearInterval(interval);
    }
  }, [isConnected, fetchOnlineUsers]);

  // Staff roster — resolves call-participant uids → real name/role for the global call UI.
  const [staffRoster, setStaffRoster] = useState([]);
  const fetchStaffRoster = useCallback(async () => {
    if (!currentBusiness?.brandId || !makeAuthenticatedRequest) return;
    try {
      const r = await makeAuthenticatedRequest(`/api/v1/staff/business/${currentBusiness.brandId}`);
      if (r.ok) {
        const d = await r.json();
        setStaffRoster(Array.isArray(d?.staff) ? d.staff : []);
      }
    } catch (e) {
      console.error('Failed to fetch staff roster:', e);
    }
  }, [currentBusiness, makeAuthenticatedRequest]);
  useEffect(() => { fetchStaffRoster(); }, [fetchStaffRoster]);
  const resolveStaff = useCallback((uid) => {
    const base = (uid || '').split('__')[0];
    const m = staffRoster.find((s) => (s.firebase_uid || s.id) === base);
    return m ? { name: m.name, role: m.role } : null;
  }, [staffRoster]);

  // Log call-state transitions for the diagnostic (idle/connecting/connected/ended + ring/incoming).
  useEffect(() => {
    dbg('[WEBRTC-DIAG] state', JSON.stringify({ callStatus, incoming: !!incomingCall, current: !!currentCall, onHold: isOnHold }));
  }, [callStatus, incomingCall, currentCall, isOnHold]);

  // P0 (web accept/media): canonical customer-handoff ACCEPT owned by the app-scoped provider so the
  // GLOBAL modal actually connects audio (was navigate-only). Same backend accept endpoint + joinMediaRoom
  // the Operator Workspace uses. Single-flight; on winner → stop ring, clear modal, start the answerer
  // media room (customer offer → local mic → answer → ICE); on lost race / error → clear safely, no media.
  const acceptIncomingHandoff = async (handoff) => {
    const h = handoff || incomingHandoff;
    const biz = currentBusiness?.brandId;
    if (!h || !h.interaction_id || !biz) return { ok: false, reason: 'no_handoff' };
    if (acceptingHandoffRef.current) return { ok: false, reason: 'in_flight' }; // single-flight (double-click)
    acceptingHandoffRef.current = true;
    try {
      const resp = await makeAuthenticatedRequest(
        `/api/v1/operator-workspace/${biz}/interactions/${h.interaction_id}/accept`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' } }
      );
      // Terminal for the alert either way: stop ringing + clear the modal, mark resolved (no re-ring).
      handoffSeenRef.current.set(h.interaction_id, 'resolved');
      setIncomingHandoff(null);
      stopRingtone();
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        const detail = err && err.detail;
        const reason = (detail && typeof detail === 'object') ? detail.reason : detail;
        return { ok: false, status: resp.status, reason: reason || `http_${resp.status}` }; // lost race / not eligible — no media
      }
      const data = await resp.json().catch(() => ({}));
      if (data && data.media_join) {
        // Winner → join the shared media room as ANSWERER; audio then flows via the global ActiveCallInterface.
        joinMediaRoom(data.media_join, {
          interactionId: h.interaction_id,
          businessId: biz,
          customerName: h.customer_name || 'Customer',
        });
        return { ok: true, media: true };
      }
      return { ok: true, media: false };
    } catch (e) {
      setIncomingHandoff(null);
      stopRingtone();
      return { ok: false, error: String((e && e.message) || e) };
    } finally {
      acceptingHandoffRef.current = false;
    }
  };

  // Ring while an incoming 1:1 call OR a global customer-handoff alert is pending; stop on
  // accept/reject/cancel/end/terminal. Global handoff only populates off the Operator Workspace page,
  // so this never double-rings with the desktop's own queue beep.
  useEffect(() => { incomingCallRef.current = incomingCall; }, [incomingCall]);
  useEffect(() => { currentCallRef.current = currentCall; }, [currentCall]);

  useEffect(() => {
    if (incomingCall || incomingHandoff || operatorCallRing || operatorCallRinging) startRingtone();
    else stopRingtone();
    return () => stopRingtone();
  }, [incomingCall, incomingHandoff, operatorCallRing, operatorCallRinging]);


  // RELEASE THE CAPTURE DEVICE IF THIS PROVIDER EVER UNMOUNTS (logout / app teardown) WITH A LIVE
  // STREAM. Deliberately NOT endCall(): unmount must not emit call-end signalling to a peer. Stopping
  // the tracks is the part that matters, because a leaked microphone outlives the component and the
  // next capture contends with it. Reads the REF, which is the live handle (the state is stale in
  // closures), and is safe to run when there is nothing to release.
  useEffect(() => () => {
    const live = localStreamRef.current;
    if (live && typeof live.getTracks === 'function') {
      live.getTracks().forEach((t) => { try { t.stop(); } catch (e) { /* already ended */ } });
    }
    localStreamRef.current = null;
  }, []);

  return {
    // Connection state
    isConnected,
    onlineUsers,
    // Diagnostic instrumentation (temporary)
    diag,
    iceInfo,
    wsReconnects,
    staffRoster,
    fetchStaffRoster,
    resolveStaff,
    // ADDITIVE — shared signaling plumbing for the conference module (no effect on 1:1 calls)
    selfId,
    // The socket-REGISTERED connection id, or null when this device has no live socket.
    //
    // WHY THIS EXISTS. `/operator-call/.../accept` still tolerates a bodyless accept so an older
    // client keeps working, but on that path the server cannot exclude the winner from its own
    // `answered_elsewhere` fan-out: the winner's WS cancel lands mid-accept and tears down the media
    // it just won. Debugger-proven on Android (enc_appt_fbeecb87bfd3) and fixed there by sending the
    // id; Business Web was the last plane still on the tolerated path.
    //
    // A FUNCTION, not a value: it is read at click time, so a socket that dropped between render and
    // Answer cannot supply a dead id. `selfId` is exactly what the WS endpoint registered
    // (`/api/v1/webrtc/{brandId}/{selfId}`) — never a client-composed identifier, and never an
    // authentication credential: the bearer identity remains authoritative and the server rejects an
    // id it does not own with a hard 400.
    getLiveConnectionId: () => (ws.current?.readyState === WebSocket.OPEN ? selfId : null),
    baseUid,
    brandId: currentBusiness?.brandId || null,
    iceServers,
    wsSend: (obj) => { try { if (ws.current?.readyState === WebSocket.OPEN) { ws.current.send(JSON.stringify(obj)); return true; } } catch (e) {} return false; },
    setExternalConfHandler: (fn) => { externalConfHandlerRef.current = fn; },
    setExternalOperatorCallHandler: (fn) => { externalOperatorCallHandlerRef.current = fn; },
    setExternalHandoffHandler: (fn) => { externalHandoffHandlerRef.current = fn; },
    // ADDITIVE (Phase C): AI-Voice human-handoff media bridge (operator = answerer). No effect on 1:1 calls.
    joinMediaRoom,
    mediaPhase,
    inCall: !!currentCall, // conference should not start during a 1:1 call (and vice versa)
    
    // Call state
    currentCall,
    incomingCall,
    // P0-D: global customer-handoff alert (rings on any Business route) + dismiss (marks resolved so it
    // will not re-ring). The Operator Workspace still owns rich accept/media; the global modal routes there.
    incomingHandoff,
    // The off-route operator-call ring, its in-place accept, and a single-use claim for the workspace.
    operatorCallRing,
    // ONE canonical accept (lib/operatorCallAccept), the same one UnifiedAgentDesktop calls. Accepts IN
    // PLACE: the Answer click is a trusted gesture that grants audio autoplay, and navigating away drops
    // it — the regression IncomingHandoffModal's own comment records. Media only after the server says won.
    acceptOperatorCallRing: async () => {
      const ring = operatorCallRing;
      if (!ring || !ring.call_session_id) return { outcome: 'failed' };
      const biz = currentBusiness?.brandId || ring.business_id;
      const connId = (ws.current?.readyState === WebSocket.OPEN) ? selfId : null;
      const res = await acceptOperatorCallSession({
        request: makeAuthenticatedRequest,
        businessId: biz,
        sessionId: ring.call_session_id,
        connectionId: connId,
      });
      if (res.outcome === 'won') {
        opCallSeenRef.current.set(ring.call_session_id, 'resolved');
        setOperatorCallRing(null);
        joinMediaRoom(mediaRoomContractFor(res.data, ring),
          { businessId: biz, customerName: ring.customer_name || null });
      } else if (res.outcome === 'answered_elsewhere') {
        opCallSeenRef.current.set(ring.call_session_id, 'resolved');
        opCallClaimedRef.current.delete(ring.call_session_id);
        setOperatorCallRing(null);
      }
      return res;
    },
    // The Operator Workspace clears the ring when it resolves an invite by DECLINING (accepting stops it
    // via joinMediaRoom). Exposed rather than duplicated so there stays one ringtone owner.
    clearOperatorCallRinging: () => setOperatorCallRinging(null),
    claimOperatorCallRing: () => {
      let claimed = null;
      setOperatorCallRing((cur) => { claimed = cur; return null; });
      return claimed;
    },
    dismissIncomingHandoff: () => setIncomingHandoff((cur) => {
      if (cur) handoffSeenRef.current.set(cur.interaction_id, 'resolved');
      return null;
    }),
    // Canonical provider-owned handoff accept (used by the global modal; connects real media).
    acceptIncomingHandoff,
    callStatus,
    // P0 (Business Web team calls): inline, device-aware call-state error (replaces blocking alerts).
    callError,
    clearCallError: () => setCallError(null),
    isReconnecting,
    isOnHold,
    localStream,
    activeCallId,
    remoteStream,
    hasRemoteAudio,   // P0-E: real live remote audio present (media-truth for the handoff desk)

    // Video refs
    localVideoRef,
    remoteVideoRef,
    
    // Call functions
    initiateCall,
    acceptCall,
    rejectCall,
    endCall,
    toggleMute,
    toggleVideo,
    toggleHold,
    startScreenShare,

    // Utility functions
    fetchOnlineUsers
  };
};
